import React, { useEffect, useMemo, useState } from "react";

/* ----------------------------- paths ----------------------------- */
const BASE = import.meta?.env?.BASE_URL ?? "/";

const NASCAR_DATA_SOURCES = {
  cup: `${BASE}data/nascar/cup/latest/data.json`,
  xfinity: `${BASE}data/nascar/xfinity/latest/data.json`,
  trucks: `${BASE}data/nascar/trucks/latest/data.json`,
};

// projections (for Driver Cards / Profiles)
const NASCAR_PROJ_SOURCES = {
  cup: `${BASE}data/nascar/cup/latest/projections.json`,
  xfinity: `${BASE}data/nascar/xfinity/latest/projections.json`,
  trucks: `${BASE}data/nascar/trucks/latest/projections.json`,
};

// new combined loop-data JSON used ONLY by the clickable Race Log modal
const NASCAR_RACE_LOG_MODAL_SOURCE = `${BASE}data/nascar/NASCAR%20Loop%20Data.json`;

const TITLES = {
  cup: "NASCAR — Cup Data",
  xfinity: "NASCAR — O'Reilly Data",
  trucks: "NASCAR — Trucks Data",
};

const NUM_LOGO_BASE = `${BASE}logos/nascar`;
const MAKE_LOGO_BASE = `${BASE}logos/nascar`;

/* ----------------------------- track groups ----------------------------- */
/* ----------------------------- track groups ----------------------------- */
const TRACK_GROUPS = {
  superspeedways: [
    "Daytona International Speedway",
    "Atlanta Motor Speedway",
    "Talladega Superspeedway",
  ],

  road_courses: [
    "Charlotte Motor Speedway Road Course",
    "Circuit of The Americas",
    "st pete",
    "Lime Rock Park",
    "san diego",
    "sebring",
    "Indianapolis Motor Speedway Road Course",
    "Sonoma Raceway",
    "Watkins Glen International",
    "Autódromo Hermanos Rodríguez",
    "Chicago Street Race",
    "Portland International Raceway",
  ],

  intermediates: [
    "Charlotte Motor Speedway",
    "Chicagoland Speedway",
    "Darlington Raceway",
    "Dover International Speedway",
    "Homestead-Miami Speedway",
    "Kansas Speedway",
    "Las Vegas Motor Speedway",
    "Michigan International Speedway",
    "Nashville",
    "Nashville Speedway",
    "Nashville Fairgrounds Speedway",
    "Nashville Superspeedway",
    "Rockingham Speedway",
    "Texas Motor Speedway",
  ],

  concrete: [
    "Bristol Motor Speedway",
    "Nashville",
    "Nashville Speedway",
    "Nashville Fairgrounds Speedway",
    "Nashville Superspeedway",
    "Dover International Speedway",
  ],

  short: [
    "Bristol Motor Speedway",
    "Dover International Speedway",
    "Iowa Speedway",
    "Lucas Oil Indianapolis Raceway Park",
    "Martinsville Speedway",
    "New Hampshire Motor Speedway",
    "North Wilkesboro Speedway",
    "Rockingham Speedway",
    "Phoenix Raceway",
    "Richmond Raceway",
    "World Wide Technology Raceway at Gateway",
    "World Wide Technology Raceway",
  ],

  short_flat: [
    "Iowa Speedway",
    "Lucas Oil Indianapolis Raceway Park",
    "Martinsville Speedway",
    "New Hampshire Motor Speedway",
    "North Wilkesboro Speedway",
    "Phoenix Raceway",
    "Richmond Raceway",
    "World Wide Technology Raceway at Gateway",
    "World Wide Technology Raceway",
  ],

  large_oval: [
    "Indianapolis Motor Speedway",
    "Michigan International Speedway",
    "Pocono Raceway",
  ],
};

const PROFILE_TRACK_TYPES = [
  "superspeedways",
  "road_courses",
  "intermediates",
  "short",
  "short_flat",
  "concrete",
];

/* ----------------------------- UI THEME ----------------------------- */
const LIGHT_THEME = {
  radius: "rounded-2xl",
  headerBg: "bg-slate-50",
  bandBg: "bg-slate-100",
  zebraOdd: "bg-white",
  zebraEven: "bg-slate-50",
  border: "border-slate-200",
  bandBorder: "border-slate-300",
  text: "text-slate-900",
  subtext: "text-slate-600",
  card: "border border-slate-200 bg-white shadow-sm",
  page: "min-h-screen bg-slate-100 text-slate-900",
};

const DARK_THEME = {
  radius: "rounded-2xl",
  headerBg: "bg-slate-900",
  bandBg: "bg-slate-900",
  zebraOdd: "bg-slate-950",
  zebraEven: "bg-slate-900",
  border: "border-slate-800/80",
  bandBorder: "border-slate-700/80",
  text: "text-slate-100",
  subtext: "text-slate-400",
  card:
    "border border-slate-800 bg-slate-900/95 shadow-sm",
  page: "min-h-screen bg-slate-950 text-slate-100",
};

const CELL_PAD = "px-1 py-0";
const HEADER_PAD = "px-2 py-1.5";
const TEXT_SZ = "text-[11.5px]";

/* ----------------------------- helpers ----------------------------- */
const norm = (s) => String(s ?? "").trim();
const lower = (s) => norm(s).toLowerCase();
const keynorm = (s) => lower(String(s).replace(/[\s._%]/g, ""));
const stripDupSuffix = (s) => String(s).replace(/_\d+$/, "");

function parseNumericLike(v) {
  if (v == null) return null;
  const s = String(v).trim();
  if (s === "") return null;
  if (/%$/.test(s)) return Number(s.replace(/%/g, "")) ?? null;
  const n = Number(s.replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function fmtCell(v) {
  const raw = norm(v);
  if (!raw) return "";
  if (/%$/.test(raw)) return raw;
  const n = parseNumericLike(raw);
  if (n == null) return raw;
  const f = n.toFixed(1);
  return f.endsWith(".0") ? f.slice(0, -2) : f;
}

function pickCol(cols, patterns) {
  for (const p of patterns) {
    const re = p instanceof RegExp ? p : new RegExp(p, "i");
    const hit = cols.find((c) => re.test(String(c)));
    if (hit) return hit;
  }
  return null;
}

function getDriverName(row) {
  return row?.Driver ?? row?.Name ?? row?.["Driver Name"] ?? row?.["Name"] ?? "";
}

function normalizeDriverKey(name, { stripSuffix = false } = {}) {
  let s = String(name ?? "").toLowerCase().trim();
  s = s.replace(/[.,'’\-]/g, " ");
  s = s.replace(/\s+/g, " ").trim();
  if (stripSuffix) s = s.replace(/\s+(jr|sr|ii|iii|iv|v)\s*$/i, "").trim();
  return s;
}

const DRIVER_NAME_ALIASES = new Map([
  ["john h nemechek", "john hunter nemechek"],
  ["john hunter nemechek", "john hunter nemechek"],
]);

function canonicalDriverKey(name, { stripSuffix = false } = {}) {
  const key = normalizeDriverKey(name, { stripSuffix });
  return DRIVER_NAME_ALIASES.get(key) || key;
}

function driverNameMatches(a, b) {
  const aFull = canonicalDriverKey(a, { stripSuffix: false });
  const bFull = canonicalDriverKey(b, { stripSuffix: false });
  if (aFull && bFull && aFull === bFull) return true;

  const aNo = canonicalDriverKey(a, { stripSuffix: true });
  const bNo = canonicalDriverKey(b, { stripSuffix: true });
  return aNo && bNo && aNo === bNo;
}

function slugify(s) {
  return String(s ?? "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function average(nums) {
  const arr = nums.filter((n) => Number.isFinite(n));
  if (!arr.length) return null;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

function safePctDiff(a, b) {
  if (!Number.isFinite(a) || !Number.isFinite(b) || b === 0) return null;
  return ((a - b) / Math.abs(b)) * 100;
}

function percentileRank(values, value, higherBetter = true) {
  const arr = values.filter((n) => Number.isFinite(n));
  if (!arr.length || !Number.isFinite(value)) return null;
  let score = 0;
  for (const v of arr) {
    if (higherBetter) {
      if (value > v) score += 1;
      else if (value === v) score += 0.5;
    } else {
      if (value < v) score += 1;
      else if (value === v) score += 0.5;
    }
  }
  return (score / arr.length) * 100;
}

function weightedAverage(items) {
  let sum = 0;
  let weight = 0;
  for (const { value, weight: w } of items) {
    if (!Number.isFinite(value)) continue;
    sum += value * w;
    weight += w;
  }
  return weight ? sum / weight : null;
}

function normTrackKey(s) {
  return String(s ?? "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const TRACK_NAME_TO_TYPES = (() => {
  const map = new Map();
  for (const [typeKey, names] of Object.entries(TRACK_GROUPS)) {
    for (const name of names) {
      const key = normTrackKey(name);
      if (!key) continue;
      if (!map.has(key)) map.set(key, new Set());
      map.get(key).add(typeKey);
    }
  }
  return map;
})();

function trackTypesForName(trackName) {
  const key = normTrackKey(trackName);
  if (!key) return [];

  const exactTypes = TRACK_NAME_TO_TYPES.get(key);
  if (exactTypes) return Array.from(exactTypes);

  // Handles shortened feed names like "Nashville" when the group list says
  // "Nashville Superspeedway", or vice versa.
  const partialTypes = new Set();
  for (const [trackKey, types] of TRACK_NAME_TO_TYPES.entries()) {
    if (!trackKey || trackKey.length < 4) continue;
    if (trackKey.includes(key) || key.includes(trackKey)) {
      for (const type of types) partialTypes.add(type);
    }
  }

  return Array.from(partialTypes);
}

function prettyTrackType(typeKey) {
  const labels = {
    superspeedways: "Superspeedways",
    road_courses: "Road Courses",
    intermediates: "Intermediates",
    short: "Short Tracks",
    short_flat: "Short Flat",
    concrete: "Concrete",
    large_oval: "Large Ovals",
  };
  return labels[typeKey] || typeKey;
}

function driverFileBase(name) {
  return String(name ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[.'’\-]/g, "")
    .trim();
}

function driverPhotoCandidates(name) {
  const base = driverFileBase(name);
  if (!base) return [];

  const underscoreExact = base.replace(/\s+/g, "_");
  const underscoreLower = underscoreExact.toLowerCase();
  const slug = slugify(name);

  return [
    `${BASE}logos/nascar/Drivers/${underscoreExact}.png`,
    `${BASE}logos/nascar/Drivers/${underscoreLower}.png`,
    `${BASE}logos/nascar/drivers/${underscoreExact}.png`,
    `${BASE}logos/nascar/drivers/${underscoreLower}.png`,
    `${BASE}images/nascar/drivers/${underscoreExact}.png`,
    `${BASE}images/nascar/drivers/${underscoreLower}.png`,
    `${BASE}images/nascar/drivers/${slug}.png`,
  ];
}

/* ----------------------------- Race Log Modal ----------------------------- */
function fmt1(v) {
  const n = _toNum(v);
  const r = Math.round(n * 10) / 10;
  return r;
}
function _toNum(v) {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}
function _dkFinishPts(finish) {
  const f = Math.round(_toNum(finish));
  if (!f || f < 1) return 0;
  if (f === 1) return 45;
  if (f === 2) return 42;
  if (f === 3) return 41;
  if (f === 4) return 40;
  if (f === 5) return 39;
  if (f === 6) return 38;
  if (f === 7) return 37;
  if (f === 8) return 36;
  if (f === 9) return 35;
  if (f === 10) return 34;
  if (f === 11) return 32;
  if (f === 12) return 31;
  if (f === 13) return 30;
  if (f === 14) return 29;
  if (f === 15) return 28;
  if (f === 16) return 27;
  if (f === 17) return 26;
  if (f === 18) return 25;
  if (f === 19) return 24;
  if (f === 20) return 23;
  if (f === 21) return 21;
  if (f === 22) return 20;
  if (f === 23) return 19;
  if (f === 24) return 18;
  if (f === 25) return 17;
  if (f === 26) return 16;
  if (f === 27) return 15;
  if (f === 28) return 14;
  if (f === 29) return 13;
  if (f === 30) return 12;
  if (f === 31) return 10;
  if (f === 32) return 9;
  if (f === 33) return 8;
  if (f === 34) return 7;
  if (f === 35) return 6;
  if (f === 36) return 5;
  if (f === 37) return 4;
  if (f === 38) return 3;
  if (f === 39) return 2;
  if (f === 40) return 1;
  return 0;
}
function _fdFinishPts(finish) {
  const f = Math.round(_toNum(finish));
  if (!f || f < 1) return 0;
  if (f === 1) return 43;
  if (f === 2) return 40;
  if (f === 3) return 38;
  if (f === 4) return 37;
  if (f === 5) return 36;
  if (f === 6) return 35;
  if (f === 7) return 34;
  if (f === 8) return 33;
  if (f === 9) return 32;
  if (f === 10) return 31;
  if (f === 11) return 30;
  if (f === 12) return 29;
  if (f === 13) return 28;
  if (f === 14) return 27;
  if (f === 15) return 26;
  if (f === 16) return 25;
  if (f === 17) return 24;
  if (f === 18) return 23;
  if (f === 19) return 22;
  if (f === 20) return 21;
  if (f === 21) return 20;
  if (f === 22) return 19;
  if (f === 23) return 18;
  if (f === 24) return 17;
  if (f === 25) return 16;
  if (f === 26) return 15;
  if (f === 27) return 14;
  if (f === 28) return 13;
  if (f === 29) return 12;
  if (f === 30) return 11;
  if (f === 31) return 10;
  if (f === 32) return 9;
  if (f === 33) return 8;
  if (f === 34) return 7;
  if (f === 35) return 6;
  if (f === 36) return 5;
  if (f === 37) return 4;
  if (f === 38) return 3;
  if (f === 39) return 2;
  if (f === 40) return 1;
  return 0;
}
function _calcDkPts({ finish, start, fastest_lap, laps_led }) {
  const fin = _toNum(finish);
  const st = _toNum(start);
  const pd = st && fin ? st - fin : 0;
  return _dkFinishPts(fin) + pd * 1 + _toNum(fastest_lap) * 0.45 + _toNum(laps_led) * 0.25;
}
function _calcFdPts({ finish, start, laps, laps_led }) {
  const fin = _toNum(finish);
  const st = _toNum(start);
  const pd = st && fin ? st - fin : 0;
  return _fdFinishPts(fin) + pd * 0.5 + _toNum(laps) * 0.1 + _toNum(laps_led) * 0.1;
}

function isDriverColumnName(colName) {
  const k = String(colName || "").trim().toLowerCase();
  return k === "driver" || k === "name" || k === "driver name";
}

function seriesLabelFromKey(key) {
  const k = String(key || "").toLowerCase();
  if (k === "xfinity") return "O'Reilly";
  if (k === "trucks") return "Trucks";
  return "Cup";
}

function normalizeSeriesName(value) {
  const k = String(value ?? "")
    .toLowerCase()
    .replace(/[’']/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  // Backend naming changed from Xfinity to O'Reilly. Treat both as one series.
  if (k.includes("xfinity") || k.includes("oreilly")) return "oreilly";
  if (k.includes("truck")) return "trucks";
  if (k.includes("cup")) return "cup";
  return k;
}

function seriesMatches(a, b) {
  const aa = normalizeSeriesName(a);
  const bb = normalizeSeriesName(b);
  return Boolean(aa && bb && aa === bb);
}

function ratioPct(num, den) {
  const n = parseNumericLike(num);
  const d = parseNumericLike(den);
  if (!Number.isFinite(n) || !Number.isFinite(d) || d <= 0) return null;
  return (n / d) * 100;
}

function safeYearFromDate(iso) {
  const s = String(iso || "");
  const y = s.slice(0, 4);
  return /^\d{4}$/.test(y) ? y : "";
}

function shortMD(iso) {
  const s = String(iso || "");
  if (s.length >= 10) return `${s.slice(5, 7)}/${s.slice(8, 10)}`;
  return s;
}

function na(v) {
  const raw = norm(v);
  return raw ? raw : "N/A";
}

function adaptNewLoopRaceLogRow(r) {
  if (!r || typeof r !== "object") return null;

  const laps = r?.LapsCompleted ?? r?.laps ?? null;
  const lapsLed = r?.LapsLed ?? r?.laps_led ?? null;
  const greenFlagPasses = r?.GreenFlagPasses ?? r?.green_flag_passes ?? null;
  const qualityPasses = r?.QualityPasses ?? r?.quality_passes ?? null;
  const top15Laps = r?.Top15Laps ?? r?.top_15_laps ?? null;

  // Convert the new combined Loop Data row into the legacy-shaped contract
  // used by Profiles, Ratings and the clickable Race Log modal.
  return {
    series: r?.Series ?? r?.series ?? "",
    race_date: r?.RaceDate ?? r?.race_date ?? "",
    track_name: r?.Track ?? r?.track_name ?? "",
    race_name: r?.RaceName ?? r?.race_name ?? "",
    race_id: r?.RaceID ?? r?.race_id ?? "",
    driver_name: r?.Driver ?? r?.driver_name ?? "",
    driver_id: r?.DriverID ?? r?.driver_id ?? "",

    start: r?.Start ?? r?.start ?? null,
    finish: r?.Finish ?? r?.finish ?? null,
    avg: r?.AvgRunningPos ?? r?.avg ?? null,
    rating: r?.DriverRating ?? r?.rating ?? null,
    laps_led: lapsLed,
    fastest_lap: r?.FastestLaps ?? r?.fastest_lap ?? null,
    laps,

    green_flag_passing_diff:
      r?.PassDifferential ?? r?.green_flag_passing_diff ?? null,
    green_flag_passes: greenFlagPasses,
    green_flag_times_passed:
      r?.GreenFlagPassed ?? r?.green_flag_times_passed ?? null,
    quality_passes: qualityPasses,
    top_15_laps: top15Laps,

    // Profile/Rating inputs. Prefer backend percentages when present;
    // otherwise derive them from the raw loop counts in the new dataset.
    pct_quality_passes:
      r?.PctQualityPasses ??
      r?.pct_quality_passes ??
      ratioPct(qualityPasses, greenFlagPasses),
    pct_top_15_laps:
      r?.PctTop15Laps ??
      r?.pct_top_15_laps ??
      ratioPct(top15Laps, laps),
    pct_laps_led:
      r?.PctLapsLed ??
      r?.pct_laps_led ??
      ratioPct(lapsLed, laps),

    status: r?.Status ?? r?.status ?? "",
    car: r?.Car ?? r?.car ?? "",
    team: r?.Team ?? r?.team ?? "",
    manufacturer: r?.Manufacturer ?? r?.manufacturer ?? "",
    sponsor: r?.Sponsor ?? r?.sponsor ?? "",
    crew_chief: r?.CrewChief ?? r?.crew_chief ?? "",
    gfs: r?.GFS ?? r?.gfs ?? null,
    gfs_rank:
      r?.GFSRank ??
      r?.["GFS Rank"] ??
      r?.gfs_rank ??
      r?.PennRank ??
      r?.["Penn Rank"] ??
      r?.penn_rank ??
      null,
    gf_laps: r?.GFLaps ?? r?.gf_laps ?? null,
    cautions: r?.Cautions ?? r?.cautions ?? null,
    dnf: r?.DNF ?? r?.dnf ?? null,

    // If the combined backend eventually supplies fantasy points, preserve them.
    dk_pts: r?.DKPts ?? r?.DKPoints ?? r?.dk_pts ?? r?.dk_points ?? null,
    fd_pts: r?.FDPts ?? r?.FDPoints ?? r?.fd_pts ?? r?.fd_points ?? null,
  };
}

function normalizeRaceRow(r) {
  const start = parseNumericLike(r?.start);
  const finish = parseNumericLike(r?.finish);
  const avg = parseNumericLike(r?.avg);
  const ll = parseNumericLike(r?.laps_led);
  const fl = parseNumericLike(r?.fastest_lap);
  const laps = parseNumericLike(r?.laps);

  const dkPtsRaw = parseNumericLike(
    r?.dk_pts ?? r?.dk ?? r?.dk_points ?? r?.dkPts ?? r?.dk_points_total
  );
  const fdPtsRaw = parseNumericLike(
    r?.fd_pts ?? r?.fd ?? r?.fd_points ?? r?.fdPts ?? r?.fd_points_total
  );

  const dk =
    dkPtsRaw != null && dkPtsRaw !== ""
      ? dkPtsRaw
      : _calcDkPts({ finish, start, fastest_lap: fl, laps_led: ll });
  const fd =
    fdPtsRaw != null && fdPtsRaw !== ""
      ? fdPtsRaw
      : _calcFdPts({ finish, start, laps, laps_led: ll });

  return {
    date: r?.race_date || "",
    track: r?.track_name || "",
    start,
    finish,
    avgRun: avg,
    rating: parseNumericLike(r?.rating),
    lapsLed: ll,
    fastLaps: fl,
    laps,
    dk,
    fd,
  };
}

function DriverRaceLogModal({ open, onClose, driverName, seriesKey, raceLogs }) {
  const YEARS = [2026, 2025, 2024, 2023, 2022, 2021, 2020];
  const seriesLabel = useMemo(() => seriesLabelFromKey(seriesKey), [seriesKey]);

  const driverRows = useMemo(() => {
    if (!open) return [];
    const dn = String(driverName || "").trim();
    if (!dn) return [];
    return (raceLogs || []).filter(
      (r) => seriesMatches(r?.series, seriesLabel) && driverNameMatches(r?.driver_name, dn)
    );
  }, [open, driverName, raceLogs, seriesLabel]);

  const yearsWithData = useMemo(() => {
    const set = new Set();
    for (const r of driverRows) {
      const y = safeYearFromDate(r?.race_date);
      if (y) set.add(Number(y));
    }
    return Array.from(set).sort((a, b) => b - a);
  }, [driverRows]);

  const defaultYear = yearsWithData.length ? yearsWithData[0] : YEARS[0];
  const [activeYear, setActiveYear] = useState(defaultYear);

  useEffect(() => {
    if (!open) return;
    setActiveYear(defaultYear);
  }, [open, defaultYear, driverName, seriesLabel]);

  const yearRows = useMemo(() => {
    const y = String(activeYear);
    const filtered = driverRows.filter((r) => safeYearFromDate(r?.race_date) === y);
    filtered.sort((a, b) =>
      String(a?.race_date || "").localeCompare(String(b?.race_date || ""))
    );
    return filtered.map(normalizeRaceRow);
  }, [driverRows, activeYear]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200]">
      <button
        type="button"
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close race log"
      />

      <div className="absolute left-1/2 top-1/2 w-[min(1040px,94vw)] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-slate-800 bg-slate-950 shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-800 bg-slate-950/90">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.18em] text-sky-400 font-bold">
              Race Logs • {seriesLabel}
            </div>
            <div className="text-xl md:text-2xl font-extrabold text-slate-100 truncate">
              {driverName || "Driver"}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded-full border border-slate-700 bg-slate-900/70 text-slate-100 text-xs font-semibold hover:bg-slate-800 transition"
          >
            Close
          </button>
        </div>

        <div className="px-5 py-3 border-b border-slate-800 flex flex-wrap gap-2">
          {YEARS.map((y) => {
            const active = Number(activeYear) === Number(y);
            const disabled = yearsWithData.length && !yearsWithData.includes(Number(y));
            return (
              <button
                key={y}
                type="button"
                onClick={() => setActiveYear(y)}
                disabled={disabled}
                className={
                  "px-3 py-1.5 rounded-full text-[11px] font-semibold border transition " +
                  (active
                    ? "bg-sky-500 text-slate-950 border-sky-400"
                    : disabled
                    ? "bg-slate-950 text-slate-600 border-slate-800 cursor-not-allowed"
                    : "bg-slate-900/60 text-slate-200 border-slate-700 hover:bg-slate-800")
                }
              >
                {y}
              </button>
            );
          })}
        </div>

        <div className="max-h-[70vh] overflow-auto">
          {yearRows.length === 0 ? (
            <div className="px-5 py-6 text-sm text-slate-300">
              No race log data for <span className="font-semibold">{driverName}</span> in{" "}
              <span className="font-semibold">{seriesLabel}</span> for{" "}
              <span className="font-semibold">{activeYear}</span>.
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10 bg-slate-900">
                <tr className="text-slate-200">
                  <th className="px-4 py-2 text-left whitespace-nowrap">Date</th>
                  <th className="px-4 py-2 text-left whitespace-nowrap">Race</th>
                  <th className="px-3 py-2 text-center whitespace-nowrap">Start</th>
                  <th className="px-3 py-2 text-center whitespace-nowrap">Finish</th>
                  <th className="px-3 py-2 text-center whitespace-nowrap">Avg Run</th>
                  <th className="px-3 py-2 text-center whitespace-nowrap">Laps Led</th>
                  <th className="px-3 py-2 text-center whitespace-nowrap">Fast Laps</th>
                  <th className="px-3 py-2 text-center whitespace-nowrap">DK</th>
                  <th className="px-3 py-2 text-center whitespace-nowrap">FD</th>
                </tr>
              </thead>
              <tbody>
                {yearRows.map((rr, idx) => (
                  <tr
                    key={`${rr.date}-${rr.track}-${idx}`}
                    className={idx % 2 ? "bg-slate-900" : "bg-slate-950"}
                  >
                    <td className="px-4 py-2 text-left whitespace-nowrap text-slate-100">
                      {shortMD(rr.date)}
                    </td>
                    <td className="px-4 py-2 text-left whitespace-nowrap text-slate-100">
                      {na(rr.track)}
                    </td>
                    <td className="px-3 py-2 text-center text-slate-100">{na(rr.start)}</td>
                    <td className="px-3 py-2 text-center text-slate-100">{na(rr.finish)}</td>
                    <td className="px-3 py-2 text-center text-slate-100">
                      {rr.avgRun == null ? "n/a" : fmt1(rr.avgRun).toFixed(1)}
                    </td>
                    <td className="px-3 py-2 text-center text-slate-100">{na(rr.lapsLed)}</td>
                    <td className="px-3 py-2 text-center text-slate-100">{na(rr.fastLaps)}</td>
                    <td className="px-3 py-2 text-center text-slate-100">
                      {rr.dk == null ? "n/a" : fmt1(rr.dk).toFixed(1)}
                    </td>
                    <td className="px-3 py-2 text-center text-slate-100">
                      {rr.fd == null ? "n/a" : fmt1(rr.fd).toFixed(1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-800 text-[11px] text-slate-400">
          Chronological order • Missing cells show N/A • If your race logs include dk_pts/fd_pts,
          those will show here automatically.
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- fetch ----------------------------- */
function useJson(url) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      setLoading(true);
      setErr("");
      try {
        const r = await fetch(`${url}?_=${Date.now()}`, {
          cache: "no-store",
          signal: controller.signal,
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        const arr = Array.isArray(j) ? j : j?.rows || j?.data || [];
        setData(arr);
      } catch (e) {
        if (e?.name !== "AbortError") setErr(String(e));
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [url]);

  return { data, loading, err };
}

/* ----------------------------- last updated ----------------------------- */
function useLastUpdated(mainUrl, metaUrl) {
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const h = await fetch(mainUrl, { method: "HEAD", cache: "no-store" });
        const lm = h.headers.get("last-modified");
        if (alive && lm) {
          setUpdatedAt(new Date(lm));
          return;
        }
      } catch (_) {}

      try {
        const r = await fetch(mainUrl, { cache: "no-store" });
        const lm2 = r.headers.get("last-modified");
        if (alive && lm2) {
          setUpdatedAt(new Date(lm2));
          return;
        }
      } catch (_) {}

      try {
        if (!metaUrl) return;
        const m = await fetch(`${metaUrl}?_=${Date.now()}`, {
          cache: "no-store",
        }).then((x) => x.json());
        const iso = m?.updated_iso || m?.updated_utc;
        const ep = m?.updated_epoch;
        const date = iso
          ? new Date(iso)
          : Number.isFinite(ep)
          ? new Date(ep * 1000)
          : null;
        if (alive && date && !isNaN(date)) setUpdatedAt(date);
      } catch (_) {}
    })();

    return () => {
      alive = false;
    };
  }, [mainUrl, metaUrl]);

  return updatedAt;
}

const fmtUpdated = (d) =>
  d
    ? d.toLocaleString(undefined, {
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : null;

/* ----------------------------- layout model ----------------------------- */
const ORDER = {
  "Driver Info": [
    ["Driver", "Name"],
    ["DK Salary", "DK Sal", "DK"],
    ["FD Salary", "FD Sal", "FD"],
    ["Qual", "Qual Pos", "Starting Pos", "Start"],
    ["Odds", "Bet Odds"],
  ],
  "Car Info": [
    ["Car #", "Car#", "Number", "#"],
    ["Car Make", "Make", "Manufacturer"],
    ["Team", "Race Team"],
    ["Engine", "Engine Supplier", "Engine Make", "Power Unit"],
    ["Sponsor", "Primary Sponsor"],
    ["Crew Chief", "CrewChief", "Crew Chief Name"],
  ],
  Practice: [
    ["1 Lap", "1Lap"],
    ["5 Lap", "5Lap"],
    ["10 Lap", "10Lap"],
    ["15 Lap", "15Lap"],
    ["20 Lap", "20Lap"],
    ["25 Lap", "25Lap"],
    ["30 Lap", "30Lap"],
  ],
  GFS: [["Overall", "GFS", "GFS Score"]],
  "Track History Stats": [
    ["# of Races", "Races"],
    ["Avg DK Pts"],
    ["Avg FD Pts"],
    ["Avg Finish"],
    ["Avg Running Pos", "Avg Run Pos"],
    ["Avg Driver Rating", "Avg Drv Rtg"],
    ["GFS Rank"],
    ["Wins"],
    ["T5", "Top5"],
    ["T10", "Top10"],
    ["T15", "Top15"],
    ["T20", "Top20"],
    ["Avg Laps Led", "Avg LLed"],
    ["Avg Fast Laps", "Avg FLaps"],
  ],
  "Similar Track Stats": [
    ["# of Races", "Races"],
    ["Avg DK Pts"],
    ["Avg FD Pts"],
    ["Avg Finish"],
    ["Avg Running Pos", "Avg Run Pos"],
    ["Avg Driver Rating", "Avg Drv Rtg"],
    ["GFS Rank_1"],
    ["Wins_1"],
    ["T5_1", "Top5_1"],
    ["T10_1", "Top10_1"],
    ["T15_1", "Top15_1"],
    ["T20_1", "Top20_1"],
    ["Avg Laps Led_1", "Avg LLed_1"],
    ["Avg Fast Laps_1", "Avg FLaps_1"],
  ],
  "Season Stats": [
    ["# of Races_2", "Races_2"],
    ["Avg DK Pts_2"],
    ["Avg FD Pts_2"],
    ["Avg Finish_2"],
    ["Avg Running Pos_2", "Avg Run Pos_2"],
    ["Avg Driver Rating_2", "Avg Drv Rtg_2"],
    ["GFS Rank_2"],
    ["Wins_2"],
    ["T5_2", "Top5_2"],
    ["T10_2", "Top10_2"],
    ["T15_2", "Top15_2"],
    ["T20_2", "Top20_2"],
    ["Avg Laps Led_2", "Avg LLed_2"],
    ["Avg Fast Laps_2", "Avg FLaps_2"],
  ],
};
const BANDS = [
  "Driver Info",
  "Car Info",
  "Practice",
  "GFS",
  "Track History Stats",
  "Similar Track Stats",
  "Season Stats",
];

function resolveOne(aliases, rawCols, used) {
  const cands = Array.isArray(aliases) ? aliases : [aliases];

  for (const cand of cands) {
    const wantExact = keynorm(cand);
    const wantStripped = keynorm(stripDupSuffix(cand));

    const hit = rawCols.find((rc) => {
      if (used.has(rc)) return false;

      const rawExact = keynorm(rc);
      const rawStripped = keynorm(stripDupSuffix(rc));

      return rawExact === wantExact || rawStripped === wantStripped;
    });

    if (hit) return hit;
  }

  return null;
}

function buildColumnsAndBands(rawCols) {
  const used = new Set();
  const groups = new Map(BANDS.map((b) => [b, []]));
  for (const band of BANDS) {
    for (const item of ORDER[band] || []) {
      const actual = resolveOne(item, rawCols, used);
      if (actual) {
        groups.get(band).push(actual);
        used.add(actual);
      }
    }
  }

  const overallRaw = rawCols.find((c) => keynorm(stripDupSuffix(c)) === "overall");
  if (overallRaw) {
    groups.set("GFS", [overallRaw]);
    for (const b of BANDS) {
      if (b === "GFS") continue;
      groups.set(
        b,
        groups
          .get(b)
          .filter((c) => !["overall", "gfs"].includes(keynorm(stripDupSuffix(c))))
      );
    }
  }

  const columns = BANDS.flatMap((b) => groups.get(b));
  const bands = [];
  let start = 0;
  for (const b of BANDS) {
    const n = groups.get(b).length;
    if (n > 0) {
      bands.push({ name: b, start, span: n });
      start += n;
    }
  }

  const bandCols = {};
  for (const b of BANDS) bandCols[b] = [...(groups.get(b) || [])];

  return { columns, bands, bandCols };
}

/* ----------------------------- heatmap ----------------------------- */
const LOWER_BETTER_KEYS = new Set([
  "avgfinish",
  "avgrunningpos",
  "1lap",
  "5lap",
  "10lap",
  "15lap",
  "20lap",
  "25lap",
  "30lap",
  "overall",
  "gfsrank",
  "qual",
  "qualpos",
  "startingpos",
  "start",
  "odds",
  "betodds",
]);

const HIGHER_BETTER_REGEX =
  /^(#ofraces|avgdkpts|avgfdpts|avgdriverrating|wins|t5|t10|t15|t20|avglapsled|avgfastlaps)$/;

function dirForCol(c) {
  const k = keynorm(stripDupSuffix(c));
  if (/^(dk|fd)(salary|sal)?$/.test(k)) return "higher";
  if (LOWER_BETTER_KEYS.has(k)) return "lower";
  if (HIGHER_BETTER_REGEX.test(k)) return "higher";
  return null;
}

function heatTextColor(min, max, v, dir, theme = "dark") {
  const n = parseNumericLike(v);
  if (
    n == null ||
    !Number.isFinite(min) ||
    !Number.isFinite(max) ||
    min === max ||
    !dir
  ) {
    return null;
  }

  let t = (n - min) / (max - min);
  t = Math.max(0, Math.min(1, t));
  if (dir === "lower") t = 1 - t;

  const isDark = theme === "dark";

  if (t < 0.5) {
    const u = t / 0.5;
    const h = 8 + u * 42;
    const s = isDark ? 90 : 85;
    const l = isDark ? 70 - u * 8 : 48 - u * 4;
    return `hsl(${h}, ${s}%, ${l}%)`;
  }

  const u = (t - 0.5) / 0.5;
  const h = 50 + u * 70;
  const s = isDark ? 88 : 80;
  const l = isDark ? 62 + u * 8 : 44 - u * 4;
  return `hsl(${h}, ${s}%, ${l}%)`;
}

/* ----------------------------- point spread (Charts) ----------------------------- */
function spreadPoints(points, { r = 16, iters = 10, pad = 2 } = {}) {
  const out = points.map((p) => ({ ...p }));
  const minDist = r * 2 + pad;

  for (let k = 0; k < iters; k++) {
    let moved = false;
    for (let i = 0; i < out.length; i++) {
      for (let j = i + 1; j < out.length; j++) {
        const a = out[i];
        const b = out[j];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const d = Math.hypot(dx, dy) || 0.0001;
        if (d < minDist) {
          const push = (minDist - d) / 2;
          const ux = dx / d;
          const uy = dy / d;
          a.x -= ux * push;
          a.y -= uy * push;
          b.x += ux * push;
          b.y += uy * push;
          moved = true;
        }
      }
    }
    if (!moved) break;
  }
  return out;
}

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

/* ----------------------------- radar ----------------------------- */
function RadarChart({ data, size = 340, dark = true }) {
  const chartData = (data || []).filter((d) => d?.key);
  const cx = size / 2;
  const cy = size / 2;
  const radius = size * 0.29;
  const levels = [25, 50, 75, 100];
  const total = Math.max(chartData.length, 3);

  const palette = dark
    ? {
        grid: "rgba(148,163,184,0.18)",
        axis: "rgba(148,163,184,0.24)",
        label: "rgba(226,232,240,0.94)",
        muted: "rgba(148,163,184,0.78)",
        fill: "rgba(14,165,233,0.20)",
        stroke: "rgba(56,189,248,1)",
        dot: "rgba(125,211,252,1)",
        center: "rgba(15,23,42,0.72)",
      }
    : {
        grid: "rgba(15,23,42,0.13)",
        axis: "rgba(15,23,42,0.18)",
        label: "rgba(15,23,42,0.88)",
        muted: "rgba(71,85,105,0.78)",
        fill: "rgba(14,165,233,0.15)",
        stroke: "rgba(2,132,199,1)",
        dot: "rgba(3,105,161,1)",
        center: "rgba(255,255,255,0.80)",
      };

  const pt = (value, idx, max = 100) => {
    const angle = -Math.PI / 2 + (idx * Math.PI * 2) / total;
    const safeValue = Number.isFinite(value) ? value : 0;
    const r = (clamp(safeValue, 0, max) / max) * radius;
    return {
      x: cx + Math.cos(angle) * r,
      y: cy + Math.sin(angle) * r,
      angle,
    };
  };

  const ringPoints = (level) =>
    chartData
      .map((_, i) => {
        const p = pt(level, i);
        return `${p.x},${p.y}`;
      })
      .join(" ");

  const polygonPoints = chartData
    .map((d, i) => {
      const p = pt(d.value, i);
      return `${p.x},${p.y}`;
    })
    .join(" ");

  if (!chartData.length) {
    return (
      <div
        className={
          "flex items-center justify-center text-xs " +
          (dark ? "text-slate-500" : "text-slate-500")
        }
        style={{ width: size, height: size }}
      >
        No profile data
      </div>
    );
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="block mx-auto h-auto max-w-full overflow-visible"
      role="img"
      aria-label="Driver performance by track type"
    >
      <defs>
        <filter id="radarGlow" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <circle cx={cx} cy={cy} r={radius + 10} fill={palette.center} />

      {levels.map((lvl) => (
        <g key={lvl}>
          <polygon
            points={ringPoints(lvl)}
            fill="none"
            stroke={palette.grid}
            strokeWidth={lvl === 100 ? "1.25" : "1"}
          />
          <text
            x={cx + 4}
            y={cy - (radius * lvl) / 100 + 11}
            fontSize="8"
            fill={palette.muted}
          >
            {lvl}
          </text>
        </g>
      ))}

      {chartData.map((d, i) => {
        const axis = pt(100, i);
        const labelR = radius + 27;
        const lx = cx + Math.cos(axis.angle) * labelR;
        const ly = cy + Math.sin(axis.angle) * labelR;
        const score = Number.isFinite(d.value) ? Math.round(d.value) : null;
        return (
          <g key={d.key}>
            <line x1={cx} y1={cy} x2={axis.x} y2={axis.y} stroke={palette.axis} />
            <text
              x={lx}
              y={ly - 4}
              textAnchor={lx < cx - 8 ? "end" : lx > cx + 8 ? "start" : "middle"}
              dominantBaseline="middle"
              fontSize="10"
              fontWeight="700"
              fill={palette.label}
            >
              {d.shortLabel || d.label}
            </text>
            <text
              x={lx}
              y={ly + 8}
              textAnchor={lx < cx - 8 ? "end" : lx > cx + 8 ? "start" : "middle"}
              dominantBaseline="middle"
              fontSize="8.5"
              fill={palette.muted}
            >
              {score == null ? "N/A" : `${score} • ${d.races || 0} races`}
            </text>
          </g>
        );
      })}

      <polygon
        points={polygonPoints}
        fill={palette.fill}
        stroke={palette.stroke}
        strokeWidth="2.5"
        strokeLinejoin="round"
        filter="url(#radarGlow)"
      />

      {chartData.map((d, i) => {
        const p = pt(d.value, i);
        return (
          <g key={`${d.key}-dot`}>
            <circle cx={p.x} cy={p.y} r="5.25" fill={palette.center} stroke={palette.stroke} strokeWidth="1.5" />
            <circle cx={p.x} cy={p.y} r="2.75" fill={palette.dot} />
            <title>
              {d.label}: {Number.isFinite(d.value) ? d.value.toFixed(1) : "N/A"} | {d.races || 0} races
            </title>
          </g>
        );
      })}
    </svg>
  );
}

function ProfileTabPill({ active, onClick, children, dark }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "px-3 py-1 rounded-full text-xs font-semibold transition " +
        (active
          ? "bg-sky-500 text-slate-950 shadow-sm"
          : dark
          ? "text-slate-300 hover:text-white bg-slate-900/70"
          : "text-slate-700 hover:text-slate-900 bg-slate-100")
      }
    >
      {children}
    </button>
  );
}

function DriverHeadshot({ name, initials = "", className = "", fallbackClassName = "" }) {
  const candidates = useMemo(() => driverPhotoCandidates(name), [name]);
  const [idx, setIdx] = useState(0);
  const [exhausted, setExhausted] = useState(false);

  useEffect(() => {
    setIdx(0);
    setExhausted(false);
  }, [name]);

  if (!candidates.length || exhausted) {
    return (
      <div className={fallbackClassName}>
        {initials}
      </div>
    );
  }

  return (
    <img
      src={candidates[idx]}
      alt={name || "Driver"}
      className={className}
      onError={() => {
        setIdx((prev) => {
          if (prev < candidates.length - 1) return prev + 1;
          setExhausted(true);
          return prev;
        });
      }}
    />
  );
}

/* ----------------------------- profile helpers ----------------------------- */
function aggregateDriverTrackTypeStats(raceLogs, driverName, seriesLabel, selectedYears = []) {
  const activeYears = new Set((selectedYears || []).map(String));

  const buckets = Object.fromEntries(
    PROFILE_TRACK_TYPES.map((t) => [
      t,
        {
          races: 0,
          finishes: [],
          avgRuns: [],
          ratings: [],
          dk: [],
          fd: [],
          gfsRanks: [],
          passingDiffs: [],
          qualityPassPct: [],
          top15LapPct: [],
          lapsLedPct: [],
          wins: 0,
          top5: 0,
          top10: 0,
        }
    ])
  );

  const rows = (raceLogs || []).filter((r) => {
    if (!seriesMatches(r?.series, seriesLabel)) return false;
    if (!driverNameMatches(r?.driver_name, driverName)) return false;
    if (activeYears.size) {
      const y = safeYearFromDate(r?.race_date);
      if (!activeYears.has(String(y))) return false;
    }
    return true;
  });

  for (const row of rows) {
    const types = trackTypesForName(row?.track_name || "");
    if (!types.length) continue;

    const normRow = normalizeRaceRow(row);
    const fin = parseNumericLike(normRow.finish);
    const avgRun = parseNumericLike(normRow.avgRun);
    const rating = parseNumericLike(normRow.rating);
    const dk = parseNumericLike(normRow.dk);
    const fd = parseNumericLike(normRow.fd);
    const gfsRank = parseNumericLike(row?.gfs_rank);
    const passingDiff = parseNumericLike(row?.green_flag_passing_diff);
    const qualityPassPct = parseNumericLike(row?.pct_quality_passes);
    const top15LapPct = parseNumericLike(row?.pct_top_15_laps);
    const lapsLedPct = parseNumericLike(row?.pct_laps_led);

    for (const type of types) {
      if (!buckets[type]) continue;
      const b = buckets[type];
      b.races += 1;
      if (Number.isFinite(fin)) b.finishes.push(fin);
      if (Number.isFinite(avgRun)) b.avgRuns.push(avgRun);
      if (Number.isFinite(rating)) b.ratings.push(rating);
      if (Number.isFinite(dk)) b.dk.push(dk);
      if (Number.isFinite(fd)) b.fd.push(fd);
      if (Number.isFinite(gfsRank)) b.gfsRanks.push(gfsRank);
      if (Number.isFinite(passingDiff)) b.passingDiffs.push(passingDiff);
      if (Number.isFinite(qualityPassPct)) b.qualityPassPct.push(qualityPassPct);
      if (Number.isFinite(top15LapPct)) b.top15LapPct.push(top15LapPct);
      if (Number.isFinite(lapsLedPct)) b.lapsLedPct.push(lapsLedPct);
      if (fin === 1) b.wins += 1;
      if (Number.isFinite(fin) && fin <= 5) b.top5 += 1;
      if (Number.isFinite(fin) && fin <= 10) b.top10 += 1;
    }
  }

  const out = {};
  for (const type of PROFILE_TRACK_TYPES) {
    const b = buckets[type];
    out[type] = {
      races: b.races,
      avgFinish: average(b.finishes),
      avgRun: average(b.avgRuns),
      avgRating: average(b.ratings),
      avgGfsRank: average(b.gfsRanks),
      avgPassingDiff: average(b.passingDiffs),
      avgQualityPassPct: average(b.qualityPassPct),
      avgTop15LapPct: average(b.top15LapPct),
      avgLapsLedPct: average(b.lapsLedPct),
      winRate: b.races ? (b.wins / b.races) * 100 : null,
      top5Rate: b.races ? (b.top5 / b.races) * 100 : null,
      top10Rate: b.races ? (b.top10 / b.races) * 100 : null,
      wins: b.wins,
      top5: b.top5,
      top10: b.top10,
    };
  }

  return out;
}



function DriverRatingsTable({
  profileRows,
  T,
  isDark,
  getNumLogoSrc,
  availableYears,
  selectedYears,
  onToggleYear,
  onAllYears,
}) {
  const [ratingsQuery, setRatingsQuery] = useState("");
  const [ratingsSort, setRatingsSort] = useState({ key: "superspeedways", dir: "desc" });

  const ratingRows = useMemo(() => {
    const q = ratingsQuery.trim().toLowerCase();

    const rows = (profileRows || []).map((profile) => {
      const ratingsByType = Object.fromEntries(
        (profile.radar || []).map((item) => [item.key, Number.isFinite(item?.value) ? item.value : null])
      );
      const gfsByType = Object.fromEntries(
        (profile.radar || []).map((item) => [
          item.key,
          Number.isFinite(item?.avgGfsRank) ? item.avgGfsRank : null,
        ])
      );

      return {
        id: profile.driver || String(profile.carNum ?? ""),
        driver: profile.driver || "",
        carNum: profile.carNum,
        gfsByType,
        ...ratingsByType,
      };
    });

    const filtered = rows.filter((row) => {
      if (!q) return true;
      return row.driver.toLowerCase().includes(q) || String(row.carNum ?? "").includes(q);
    });

    const dir = ratingsSort.dir === "asc" ? 1 : -1;
    filtered.sort((a, b) => {
      if (ratingsSort.key === "driver") {
        return dir * a.driver.localeCompare(b.driver, undefined, { sensitivity: "base" });
      }

      const av = parseNumericLike(a[ratingsSort.key]);
      const bv = parseNumericLike(b[ratingsSort.key]);
      if (Number.isFinite(av) && Number.isFinite(bv)) return dir * (av - bv);
      if (Number.isFinite(av)) return -1;
      if (Number.isFinite(bv)) return 1;
      return a.driver.localeCompare(b.driver, undefined, { sensitivity: "base" });
    });

    return filtered;
  }, [profileRows, ratingsQuery, ratingsSort]);

  const columns = [
    { key: "driver", label: "Driver" },
    ...PROFILE_TRACK_TYPES.map((key) => ({ key, label: prettyTrackType(key) })),
  ];

  const toggleSort = (key) => {
    setRatingsSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: key === "driver" ? "asc" : "desc" }
    );
  };

  const ratingColor = (value) => {
    if (!Number.isFinite(value)) return undefined;
    if (value >= 70) return isDark ? "rgb(110 231 183)" : "rgb(5 150 105)";
    if (value >= 50) return isDark ? "rgb(125 211 252)" : "rgb(2 132 199)";
    if (value >= 35) return isDark ? "rgb(252 211 77)" : "rgb(217 119 6)";
    return isDark ? "rgb(253 164 175)" : "rgb(225 29 72)";
  };

  const headerBg = isDark ? "bg-slate-900" : "bg-slate-100";
  const headerCell = isDark
    ? "border-r border-b border-slate-700 text-slate-200"
    : "border-r border-b border-slate-300 text-slate-700";
  const rowOdd = isDark ? "bg-slate-950" : "bg-white";
  const rowEven = isDark ? "bg-slate-900" : "bg-slate-50";

  return (
    <div className={`${T.radius} ${T.card} overflow-hidden`}>
      <div className={`flex flex-wrap items-center gap-2 border-b px-4 py-3 ${T.border}`}>
        <div className="mr-auto">
          <div className="text-sm font-bold text-sky-400">Driver Ratings</div>
          <div className={`text-xs ${T.subtext}`}>
            Track-type profile ratings using the same scoring model as Driver Profiles.
          </div>
        </div>

        <input
          value={ratingsQuery}
          onChange={(e) => setRatingsQuery(e.target.value)}
          placeholder="Search driver…"
          className={
            "h-9 w-52 rounded-full border px-3 text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 " +
            (isDark
              ? "border-slate-700 bg-slate-950/80 text-slate-100 placeholder:text-slate-500"
              : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-400")
          }
        />

        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={onAllYears}
            className={
              "h-9 rounded-full border px-3 text-xs font-semibold transition " +
              ((selectedYears || []).length === 0
                ? "border-sky-400 bg-sky-500 text-slate-950"
                : isDark
                ? "border-slate-700 bg-slate-950/80 text-slate-200 hover:bg-slate-900"
                : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50")
            }
          >
            All Years
          </button>

          {(availableYears || []).map((year) => {
            const active = (selectedYears || []).includes(year);
            return (
              <button
                key={year}
                type="button"
                onClick={() => onToggleYear?.(year)}
                className={
                  "h-9 rounded-full border px-3 text-xs font-semibold transition " +
                  (active
                    ? "border-sky-400 bg-sky-500 text-slate-950"
                    : isDark
                    ? "border-slate-700 bg-slate-950/80 text-slate-200 hover:bg-slate-900"
                    : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50")
                }
              >
                {year}
              </button>
            );
          })}
        </div>

        <div className={`text-xs ${T.subtext}`}>{ratingRows.length.toLocaleString()} drivers</div>
      </div>

      <div className="max-h-[72vh] overflow-auto">
        <table
          className="w-full min-w-[980px] table-fixed border-separate text-[11px] md:text-xs"
          style={{ borderSpacing: 0 }}
        >
          <thead className={`${headerBg} sticky top-0 z-20 shadow-[0_2px_0_0_rgba(0,0,0,0.35)]`}>
            <tr>
              {columns.map((column, index) => (
                <th
                  key={column.key}
                  onClick={() => toggleSort(column.key)}
                  className={`cursor-pointer select-none whitespace-nowrap px-3 py-2 text-center font-semibold ${headerCell} ${
                    index === 0 ? "sticky left-0 z-30 w-[230px] text-left" : ""
                  }`}
                >
                  <span className="inline-flex items-center gap-1">
                    {column.label}
                    {ratingsSort.key === column.key ? (
                      <span className="text-[10px] text-slate-400">
                        {ratingsSort.dir === "desc" ? "▼" : "▲"}
                      </span>
                    ) : (
                      <span className="text-[9px] text-slate-600">▲</span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {ratingRows.map((row, rowIndex) => {
              const rowBg = rowIndex % 2 === 0 ? rowOdd : rowEven;
              const iconSrc = row.carNum != null ? getNumLogoSrc?.(row.carNum) : null;

              return (
                <tr key={row.id} className={rowBg}>
                  <td
                    className={`sticky left-0 z-10 w-[230px] max-w-[230px] px-3 py-2 text-left ${rowBg} shadow-[inset_-6px_0_8px_-6px_rgba(0,0,0,0.6)]`}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      {iconSrc ? (
                        <img
                          src={iconSrc}
                          alt={row.carNum ?? ""}
                          className="h-5 w-5 flex-none object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="h-5 w-5 flex-none rounded-full bg-slate-700/40" />
                      )}
                      <span className="truncate font-semibold">{row.driver}</span>
                    </div>
                  </td>

                  {PROFILE_TRACK_TYPES.map((type) => {
                    const value = row[type];
                    return (
                      <td
                        key={type}
                        className="whitespace-nowrap px-3 py-2 text-center tabular-nums"
                      >
                        <div className="font-extrabold" style={{ color: ratingColor(value) }}>
                          {Number.isFinite(value) ? value.toFixed(1) : "—"}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}

            {!ratingRows.length && (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-slate-400">
                  No drivers match the current search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function DriverProfileSheet({
  profileRows,
  selectedProfileTrackType,
  T,
  isDark,
  setRaceLogDriver,
  setRaceLogOpen,
}) {
  const [sheetFilters, setSheetFilters] = useState({});
  const [sheetSort, setSheetSort] = useState({ key: "selectedScore", dir: "desc" });

  const money = (v) => {
    const n = parseNumericLike(v);
    return Number.isFinite(n)
      ? n.toLocaleString(undefined, { maximumFractionDigits: 0 })
      : "—";
  };

  const one = (v) => (Number.isFinite(v) ? v.toFixed(1) : "—");

  const getTypeHit = (p, type) => p.radar?.find((r) => r.key === type) || null;
  const typeScoreRaw = (p, type) => {
    const hit = getTypeHit(p, type);
    return Number.isFinite(hit?.value) ? hit.value : null;
  };
  const typeRacesRaw = (p, type) => {
    const hit = getTypeHit(p, type);
    return Number.isFinite(hit?.races) ? hit.races : 0;
  };
  const selectedScoreLabel =
    selectedProfileTrackType === "all"
      ? "Profile Score"
      : `${prettyTrackType(selectedProfileTrackType)} Score`;

  const scoreClass = (v) => {
    if (!Number.isFinite(v)) return "";
    if (v >= 70) return "text-emerald-300 font-bold";
    if (v >= 50) return "text-sky-300 font-bold";
    if (v >= 35) return "text-amber-300 font-bold";
    return "text-rose-300 font-bold";
  };

  const columnDefs = useMemo(
    () => [
      {
        key: "driver",
        label: "Driver",
        type: "text",
        value: (p) => p.driver || "",
        display: (p) => p.driver || "—",
        cellClass: "text-left font-semibold min-w-[190px]",
      },
      { key: "car", label: "Car", type: "number", value: (p) => p.carNum, display: (p) => p.carNum ?? "—" },
      { key: "make", label: "Make", type: "text", value: (p) => p.makeVal || "", display: (p) => p.makeVal || "—" },
      {
        key: "team",
        label: "Team",
        type: "text",
        value: (p) => p.team || "",
        display: (p) => p.team || "—",
        cellClass: "text-left min-w-[180px]",
      },
      { key: "dkSal", label: "DK Sal", type: "number", value: (p) => parseNumericLike(p.dkSal), display: (p) => money(p.dkSal) },
      { key: "fdSal", label: "FD Sal", type: "number", value: (p) => parseNumericLike(p.fdSal), display: (p) => money(p.fdSal) },
      { key: "dkProj", label: "DK Proj", type: "number", value: (p) => p.proj?.dk, display: (p) => one(p.proj?.dk) },
      { key: "fdProj", label: "FD Proj", type: "number", value: (p) => p.proj?.fd, display: (p) => one(p.proj?.fd) },
      { key: "qual", label: "Qual", type: "number", value: (p) => parseNumericLike(p.qual), display: (p) => p.qual ?? "—" },
      { key: "odds", label: "Odds", type: "text", value: (p) => p.odds ?? "", display: (p) => p.odds ?? "—" },
      { key: "practice", label: "Practice Avg", type: "number", value: (p) => p.prac, display: (p) => one(p.prac) },
      {
        key: "selectedScore",
        label: selectedScoreLabel,
        type: "number",
        value: (p) => p.selectedTrackTypeProfile,
        display: (p) => one(p.selectedTrackTypeProfile),
        cellClass: (p) => scoreClass(p.selectedTrackTypeProfile),
      },
      {
        key: "bestType",
        label: "Best Type",
        type: "text",
        value: (p) => p.bestTypes?.[0]?.label || "",
        display: (p) => p.bestTypes?.[0]?.label || "—",
        cellClass: "text-left min-w-[140px]",
      },
      {
        key: "bestScore",
        label: "Best Score",
        type: "number",
        value: (p) => p.bestTypes?.[0]?.value,
        display: (p) => one(p.bestTypes?.[0]?.value),
        cellClass: (p) => scoreClass(p.bestTypes?.[0]?.value),
      },
      {
        key: "worstType",
        label: "Worst Type",
        type: "text",
        value: (p) => p.worstTypes?.[0]?.label || "",
        display: (p) => p.worstTypes?.[0]?.label || "—",
        cellClass: "text-left min-w-[140px]",
      },
      {
        key: "worstScore",
        label: "Worst Score",
        type: "number",
        value: (p) => p.worstTypes?.[0]?.value,
        display: (p) => one(p.worstTypes?.[0]?.value),
        cellClass: (p) => scoreClass(p.worstTypes?.[0]?.value),
      },
      { key: "ssScore", label: "SS Score", type: "number", value: (p) => typeScoreRaw(p, "superspeedways"), display: (p) => one(typeScoreRaw(p, "superspeedways")) },
      { key: "ssRaces", label: "SS Races", type: "number", value: (p) => typeRacesRaw(p, "superspeedways"), display: (p) => typeRacesRaw(p, "superspeedways") },
      { key: "roadScore", label: "Road Score", type: "number", value: (p) => typeScoreRaw(p, "road_courses"), display: (p) => one(typeScoreRaw(p, "road_courses")) },
      { key: "roadRaces", label: "Road Races", type: "number", value: (p) => typeRacesRaw(p, "road_courses"), display: (p) => typeRacesRaw(p, "road_courses") },
      { key: "intScore", label: "Intermediate Score", type: "number", value: (p) => typeScoreRaw(p, "intermediates"), display: (p) => one(typeScoreRaw(p, "intermediates")) },
      { key: "intRaces", label: "Intermediate Races", type: "number", value: (p) => typeRacesRaw(p, "intermediates"), display: (p) => typeRacesRaw(p, "intermediates") },
      { key: "shortScore", label: "Short Score", type: "number", value: (p) => typeScoreRaw(p, "short"), display: (p) => one(typeScoreRaw(p, "short")) },
      { key: "shortRaces", label: "Short Races", type: "number", value: (p) => typeRacesRaw(p, "short"), display: (p) => typeRacesRaw(p, "short") },
      { key: "shortFlatScore", label: "Short Flat Score", type: "number", value: (p) => typeScoreRaw(p, "short_flat"), display: (p) => one(typeScoreRaw(p, "short_flat")) },
      { key: "shortFlatRaces", label: "Short Flat Races", type: "number", value: (p) => typeRacesRaw(p, "short_flat"), display: (p) => typeRacesRaw(p, "short_flat") },
      { key: "concreteScore", label: "Concrete Score", type: "number", value: (p) => typeScoreRaw(p, "concrete"), display: (p) => one(typeScoreRaw(p, "concrete")) },
      { key: "concreteRaces", label: "Concrete Races", type: "number", value: (p) => typeRacesRaw(p, "concrete"), display: (p) => typeRacesRaw(p, "concrete") },
    ],
    [selectedScoreLabel]
  );

  const activeFilterCount = useMemo(
    () => Object.values(sheetFilters).filter((v) => String(v || "").trim()).length,
    [sheetFilters]
  );

  const matchesNumberFilter = (rawValue, filterText) => {
    const q = String(filterText || "").trim();
    if (!q) return true;

    const n = parseNumericLike(rawValue);
    if (!Number.isFinite(n)) return false;

    const rangeMatch = q.match(/^(-?\d+(?:\.\d+)?)\s*-\s*(-?\d+(?:\.\d+)?)$/);
    if (rangeMatch) {
      const a = Number(rangeMatch[1]);
      const b = Number(rangeMatch[2]);
      const lo = Math.min(a, b);
      const hi = Math.max(a, b);
      return n >= lo && n <= hi;
    }

    const opMatch = q.match(/^(>=|<=|>|<|=)\s*(-?\d+(?:\.\d+)?)$/);
    if (opMatch) {
      const op = opMatch[1];
      const target = Number(opMatch[2]);
      if (op === ">=") return n >= target;
      if (op === "<=") return n <= target;
      if (op === ">") return n > target;
      if (op === "<") return n < target;
      return n === target;
    }

    return String(rawValue ?? "").toLowerCase().includes(q.toLowerCase());
  };

  const filteredSheetRows = useMemo(() => {
    const arr = (profileRows || []).filter((p) =>
      columnDefs.every((col) => {
        const q = String(sheetFilters[col.key] || "").trim();
        if (!q) return true;

        const rawValue = col.value(p);
        const shownValue = col.display(p);

        if (col.type === "number") {
          return matchesNumberFilter(rawValue, q) || String(shownValue ?? "").toLowerCase().includes(q.toLowerCase());
        }

        return String(shownValue ?? "").toLowerCase().includes(q.toLowerCase());
      })
    );

    const sortCol = columnDefs.find((c) => c.key === sheetSort.key) || columnDefs[0];
    const dir = sheetSort.dir === "asc" ? 1 : -1;

    arr.sort((a, b) => {
      const av = sortCol.value(a);
      const bv = sortCol.value(b);
      const an = parseNumericLike(av);
      const bn = parseNumericLike(bv);

      if (Number.isFinite(an) && Number.isFinite(bn)) return dir * (an - bn);
      if (Number.isFinite(an) && !Number.isFinite(bn)) return -1;
      if (!Number.isFinite(an) && Number.isFinite(bn)) return 1;

      return (
        dir *
        String(sortCol.display(a) ?? "").localeCompare(String(sortCol.display(b) ?? ""), undefined, {
          sensitivity: "base",
        })
      );
    });

    return arr;
  }, [profileRows, columnDefs, sheetFilters, sheetSort]);

  const setFilter = (key, value) => {
    setSheetFilters((prev) => ({ ...prev, [key]: value }));
  };

  const toggleSort = (key) => {
    setSheetSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "desc" }
    );
  };

  const headerBase =
    "px-3 py-2 text-center whitespace-nowrap border-r border-b text-[11px] uppercase tracking-[0.08em] " +
    T.border;
  const filterHeaderBase = "px-2 py-2 border-r border-b " + T.border;
  const cellBase = "px-3 py-2 text-center whitespace-nowrap border-r border-b " + T.border;
  const inputCls =
    "w-full min-w-[74px] rounded-md border px-2 py-1 text-[11px] outline-none transition " +
    (isDark
      ? "border-slate-700 bg-slate-950/80 text-slate-100 placeholder:text-slate-600 focus:border-sky-400"
      : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 focus:border-sky-500");

  return (
    <div className={`${T.radius} ${T.card} overflow-hidden`}>
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-800/80">
        <div>
          <div className="text-sm font-bold text-sky-400">Driver Profile Sheet</div>
          <div className={`text-xs ${T.subtext}`}>
            Spreadsheet-style view with sortable columns and filterable headers. Numeric filters support <span className="font-semibold">&gt;=50</span>, <span className="font-semibold">&lt;20</span>, or <span className="font-semibold">40-70</span>.
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className={`text-xs ${T.subtext}`}>
            {filteredSheetRows.length.toLocaleString()} / {profileRows.length.toLocaleString()} drivers
          </div>
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={() => setSheetFilters({})}
              className={
                "px-3 py-1.5 rounded-full border text-xs font-semibold transition " +
                (isDark
                  ? "border-slate-700 bg-slate-900/70 text-slate-200 hover:bg-slate-800"
                  : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100")
              }
            >
              Clear Filters ({activeFilterCount})
            </button>
          )}
        </div>
      </div>

      <div className="overflow-auto max-h-[72vh]">
        <table className="w-full border-separate text-[11.5px]" style={{ borderSpacing: 0 }}>
          <thead className="sticky top-0 z-30">
            <tr>
              {columnDefs.map((col, idx) => {
                const active = sheetSort.key === col.key;
                return (
                  <th
                    key={col.key}
                    className={[
                      headerBase,
                      idx === 0 ? `sticky left-0 z-40 text-left ${T.headerBg}` : T.headerBg,
                      T.text,
                    ].join(" ")}
                  >
                    <button
                      type="button"
                      onClick={() => toggleSort(col.key)}
                      className="inline-flex w-full items-center justify-center gap-1 hover:text-sky-400 transition"
                      title={`Sort by ${col.label}`}
                    >
                      <span>{col.label}</span>
                      <span className={active ? "text-sky-400" : T.subtext}>
                        {active ? (sheetSort.dir === "asc" ? "▲" : "▼") : "↕"}
                      </span>
                    </button>
                  </th>
                );
              })}
            </tr>

            <tr>
              {columnDefs.map((col, idx) => (
                <th
                  key={`${col.key}-filter`}
                  className={[
                    filterHeaderBase,
                    idx === 0 ? `sticky left-0 z-40 ${T.headerBg}` : T.headerBg,
                  ].join(" ")}
                >
                  <input
                    value={sheetFilters[col.key] || ""}
                    onChange={(e) => setFilter(col.key, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    placeholder={col.type === "number" ? ">= / range" : "filter"}
                    className={inputCls}
                  />
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {filteredSheetRows.length ? (
              filteredSheetRows.map((p, idx) => {
                const rowBg = idx % 2 ? T.zebraEven : T.zebraOdd;

                return (
                  <tr
                    key={`${p.driver}-sheet-${idx}`}
                    className={`${rowBg} ${isDark ? "hover:bg-slate-800/70" : "hover:bg-slate-100"} transition`}
                  >
                    {columnDefs.map((col, colIdx) => {
                      const extraClass =
                        typeof col.cellClass === "function" ? col.cellClass(p) : col.cellClass || "";
                      const content = col.display(p);

                      if (col.key === "driver") {
                        return (
                          <td
                            key={col.key}
                            className={[
                              cellBase,
                              extraClass,
                              "sticky left-0 z-20",
                              rowBg,
                              T.text,
                              "shadow-[inset_-8px_0_8px_-8px_rgba(0,0,0,0.45)]",
                            ].join(" ")}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                setRaceLogDriver(p.driver);
                                setRaceLogOpen(true);
                              }}
                              className="hover:underline underline-offset-4"
                            >
                              {content}
                            </button>
                          </td>
                        );
                      }

                      return (
                        <td key={col.key} className={[cellBase, T.text, extraClass].join(" ")}>
                          {content}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={columnDefs.length} className={`px-4 py-8 text-center ${T.subtext}`}>
                  No drivers match the current profile sheet filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ----------------------------- page ----------------------------- */
export default function NascarData({ series = "cup" }) {
  const key = String(series || "cup").toLowerCase();
  const src = NASCAR_DATA_SOURCES[key] || NASCAR_DATA_SOURCES.cup;
  const projSrc = NASCAR_PROJ_SOURCES[key] || NASCAR_PROJ_SOURCES.cup;
  const title = TITLES[key] || "NASCAR — Data";

  const { data, loading, err } = useJson(src);
  const { data: projDataRaw, loading: projLoading, err: projErr } = useJson(projSrc);
  const { data: raceLogModalSourceRaw } = useJson(NASCAR_RACE_LOG_MODAL_SOURCE);

  const raceLogModalRows = useMemo(
    () =>
      (raceLogModalSourceRaw || [])
        .map(adaptNewLoopRaceLogRow)
        .filter(Boolean),
    [raceLogModalSourceRaw]
  );

  const projRows = useMemo(() => {
    const j = projDataRaw;
    if (!j) return [];
    if (Array.isArray(j)) return j;
    return j?.rows || j?.data || [];
  }, [projDataRaw]);

  const projByDriver = useMemo(() => {
    const m = new Map();
    for (const r of projRows) {
      const name = getDriverName(r);
      if (!name) continue;
      m.set(normalizeDriverKey(name, { stripSuffix: false }), r);
      m.set(normalizeDriverKey(name, { stripSuffix: true }), r);
    }
    return m;
  }, [projRows]);

  const rawCols = useMemo(() => (data.length ? Object.keys(data[0]) : []), [data]);
  const { columns, bands, bandCols } = useMemo(() => buildColumnsAndBands(rawCols), [rawCols]);

  const metaUrl = src.replace(/data\.json$/, "meta.json");
  const updatedAt = useLastUpdated(src, metaUrl);

  const [theme, setTheme] = useState("dark");
  const isDark = theme === "dark";
  const T = isDark ? DARK_THEME : LIGHT_THEME;

  const [view, setView] = useState("table");
  const [raceLogOpen, setRaceLogOpen] = useState(false);
  const [raceLogDriver, setRaceLogDriver] = useState("");

  const carNumColName = useMemo(() => {
    for (const c of rawCols) {
      const kn = keynorm(stripDupSuffix(c));
      if (
        kn === "car#" ||
        kn === "number" ||
        kn === "carnumber" ||
        (/#/.test(c) && /car/i.test(c))
      ) {
        return c;
      }
    }
    return null;
  }, [rawCols]);

  const carNumNorm = carNumColName ? keynorm(stripDupSuffix(carNumColName)) : "";
  const carMakeNorms = useMemo(() => new Set(["carmake", "make", "manufacturer"]), []);

  const bandCuts = useMemo(() => {
    const want = new Set(["odds", "crewchief", "30lap", "overall", "avgfastlaps", "avgflaps"]);
    const set = new Set();
    columns.forEach((c, i) => {
      const k = keynorm(stripDupSuffix(c));
      if (want.has(k)) set.add(i);
    });
    return set;
  }, [columns]);

  const overallIndex = useMemo(() => {
    const i = columns.findIndex((c) => keynorm(stripDupSuffix(c)) === "overall");
    return i >= 0 ? i : null;
  }, [columns]);

  const [q, setQ] = useState("");
  const [selectedTeam, setSelectedTeam] = useState("");
  const [selectedManufacturer, setSelectedManufacturer] = useState("");
  const teamFilterCol = useMemo(
    () => pickCol(rawCols, [/^team$/i, /^race\s*team$/i]),
    [rawCols]
  );
  const makeFilterCol = useMemo(
    () => rawCols.find((c) => carMakeNorms.has(keynorm(stripDupSuffix(c)))) || null,
    [rawCols, carMakeNorms]
  );
  const teamOptions = useMemo(
    () => [...new Set(data.map((r) => String(r?.[teamFilterCol] ?? "").trim()).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b)),
    [data, teamFilterCol]
  );
  const manufacturerOptions = useMemo(
    () => [...new Set(data.map((r) => String(r?.[makeFilterCol] ?? "").trim()).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b)),
    [data, makeFilterCol]
  );

  useEffect(() => {
    setQ("");
    setSelectedTeam("");
    setSelectedManufacturer("");
  }, [key]);

  const filtered = useMemo(() => {
    const s = lower(q);
    return data.filter((r) => {
      const driver = lower(r.Driver || r.Name || "");
      const teamValue = String(r?.[teamFilterCol] ?? "").trim();
      const makeValue = String(r?.[makeFilterCol] ?? "").trim();
      if (selectedTeam && teamValue !== selectedTeam) return false;
      if (selectedManufacturer && makeValue !== selectedManufacturer) return false;
      return !s || driver.includes(s) || lower(teamValue).includes(s) || lower(makeValue).includes(s);
    });
  }, [data, q, teamFilterCol, makeFilterCol, selectedTeam, selectedManufacturer]);

  const defaultSortKey = columns[1] || "DK Salary";
  const [sort, setSort] = useState({ key: defaultSortKey, dir: "desc" });

  useEffect(() => {
    setSort((prev) => {
      if (columns.includes(prev.key)) return prev;
      return { key: columns[1] || columns[0] || "DK Salary", dir: "desc" };
    });
  }, [columns]);

  const onSort = (k) =>
    setSort((p) =>
      p.key === k
        ? { key: k, dir: p.dir === "asc" ? "desc" : "asc" }
        : { key: k, dir: "desc" }
    );

  function compareRows(a, b, k) {
    const av = parseNumericLike(a[k]);
    const bv = parseNumericLike(b[k]);
    if (av != null && bv != null) return av - bv;
    return String(a[k] ?? "").localeCompare(String(b[k] ?? ""), undefined, {
      sensitivity: "base",
    });
  }

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const sgn = sort.dir === "asc" ? 1 : -1;
    arr.sort((a, b) => sgn * compareRows(a, b, sort.key));
    return arr;
  }, [filtered, sort]);

  const heatStats = useMemo(() => {
    const stats = {};
    if (!sorted.length) return stats;
    for (const c of columns) {
      const dir = dirForCol(c);
      if (!dir) continue;
      let min = Infinity;
      let max = -Infinity;
      for (const r of sorted) {
        const n = parseNumericLike(r?.[c]);
        if (n == null) continue;
        if (n < min) min = n;
        if (n > max) max = n;
      }
      if (min < Infinity && max > -Infinity) stats[c] = { min, max, dir };
    }
    return stats;
  }, [sorted, columns]);

  const textSz = TEXT_SZ;
  const cellCls = `${CELL_PAD} text-center whitespace-nowrap overflow-hidden text-ellipsis ${T.text}`;
  const headerCell = `${HEADER_PAD} font-semibold text-center whitespace-nowrap cursor-pointer select-none ${T.text} ${T.headerBg}`;
  const driverMinWidth = "min-w-[16ch] md:min-w-[18ch]";

  const chartBands = useMemo(
    () => ["Track History Stats", "Similar Track Stats", "Season Stats", "Practice"],
    []
  );
  const [chartBand, setChartBand] = useState("Track History Stats");

  const chartCols = useMemo(() => {
    const cols = bandCols?.[chartBand] || [];
    return cols.filter((c) => {
      const k = keynorm(stripDupSuffix(c));
      if (k === "driver" || k === "name" || k === "team") return false;
      return true;
    });
  }, [bandCols, chartBand]);

  const [xCol, setXCol] = useState("");
  const [yCol, setYCol] = useState("");

  useEffect(() => {
    if (!chartCols.length) return;

    const defX =
      pickCol(chartCols, [/avg\s*running\s*pos/i, /avg\s*run/i, /^1\s*lap/i, /^5\s*lap/i]) ||
      chartCols[0];
    const defY =
      pickCol(chartCols, [/avg\s*driver\s*rating/i, /avg\s*dk\s*pts/i, /wins/i, /t10/i]) ||
      chartCols[Math.min(1, chartCols.length - 1)] ||
      chartCols[0];

    setXCol(defX);
    setYCol(defY);
  }, [chartBand, chartCols]);

  const autoRevX = useMemo(() => dirForCol(xCol) === "lower", [xCol]);
  const autoRevY = useMemo(() => dirForCol(yCol) === "lower", [yCol]);
  const [revX, setRevX] = useState(null);
  const [revY, setRevY] = useState(null);

  useEffect(() => setRevX(null), [xCol]);
  useEffect(() => setRevY(null), [yCol]);

  const isRevX = revX == null ? autoRevX : revX;
  const isRevY = revY == null ? autoRevY : revY;

  const chartPoints = useMemo(() => {
    if (!xCol || !yCol || !sorted.length) return [];

    const pts = [];
    for (const r of sorted) {
      const xv = parseNumericLike(r?.[xCol]);
      const yv = parseNumericLike(r?.[yCol]);
      if (xv == null || yv == null) continue;

      const driver = getDriverName(r) || "";
      const carNum = carNumColName ? parseNumericLike(r?.[carNumColName]) : null;

      pts.push({
        driver,
        driverKey: lower(driver),
        xVal: xv,
        yVal: yv,
        carNum: carNum != null ? Math.round(carNum) : null,
        rawRow: r,
      });
    }
    return pts;
  }, [sorted, xCol, yCol, carNumColName]);

  const chartStats = useMemo(() => {
    if (!chartPoints.length) return null;
    let xmin = Infinity;
    let xmax = -Infinity;
    let ymin = Infinity;
    let ymax = -Infinity;
    let sx = 0;
    let sy = 0;
    for (const p of chartPoints) {
      xmin = Math.min(xmin, p.xVal);
      xmax = Math.max(xmax, p.xVal);
      ymin = Math.min(ymin, p.yVal);
      ymax = Math.max(ymax, p.yVal);
      sx += p.xVal;
      sy += p.yVal;
    }
    const n = chartPoints.length;
    return {
      xmin,
      xmax,
      ymin,
      ymax,
      xAvg: sx / n,
      yAvg: sy / n,
    };
  }, [chartPoints]);

  const plot = useMemo(() => {
    if (!chartStats || !chartPoints.length) return null;

    const W = 980;
    const H = 540;
    const pad = { l: 56, r: 22, t: 18, b: 56 };
    const innerW = W - pad.l - pad.r;
    const innerH = H - pad.t - pad.b;

    const x0 = chartStats.xmin;
    const x1 = chartStats.xmax;
    const y0 = chartStats.ymin;
    const y1 = chartStats.ymax;

    const sx = (v) => {
      const t = (v - x0) / (x1 - x0 || 1);
      const u = isRevX ? 1 - t : t;
      return pad.l + u * innerW;
    };
    const sy = (v) => {
      const t = (v - y0) / (y1 - y0 || 1);
      const u = isRevY ? t : 1 - t;
      return pad.t + u * innerH;
    };

    const base = chartPoints.map((p) => ({
      id: p.driverKey || String(p.carNum ?? ""),
      x: sx(p.xVal),
      y: sy(p.yVal),
      ...p,
    }));

    const spread = spreadPoints(base, { r: 16, iters: 16, pad: 3 }).map((p) => ({
      ...p,
      x: clamp(p.x, pad.l, pad.l + innerW),
      y: clamp(p.y, pad.t, pad.t + innerH),
    }));

    const avgX = sx(chartStats.xAvg);
    const avgY = sy(chartStats.yAvg);

    return { W, H, pad, innerW, innerH, points: spread, avgX, avgY };
  }, [chartStats, chartPoints, isRevX, isRevY]);

  const dkSalCol = useMemo(
    () => pickCol(rawCols, [/^dk\s*sal/i, /^dk\s*salary/i, /\bdk\b.*sal/i]),
    [rawCols]
  );
  const fdSalCol = useMemo(
    () => pickCol(rawCols, [/^fd\s*sal/i, /^fd\s*salary/i, /\bfd\b.*sal/i]),
    [rawCols]
  );
  const qualCol = useMemo(
    () => pickCol(rawCols, [/^qual/i, /qual\s*pos/i, /starting\s*pos/i, /^start/i]),
    [rawCols]
  );
  const oddsCol = useMemo(() => pickCol(rawCols, [/^odds/i, /bet\s*odds/i]), [rawCols]);
  const teamCol = useMemo(() => pickCol(rawCols, [/^team$/i, /race\s*team/i]), [rawCols]);

  const practiceCols = useMemo(() => bandCols?.Practice || [], [bandCols]);
  function practiceAvg(row) {
    let s = 0;
    let n = 0;
    for (const c of practiceCols) {
      const v = parseNumericLike(row?.[c]);
      if (v == null) continue;
      s += v;
      n++;
    }
    return n ? s / n : null;
  }

  const projCols = useMemo(() => {
    const cols = projRows?.length ? Object.keys(projRows[0]) : [];
    return cols;
  }, [projRows]);

  const dkProjCol = useMemo(
    () => pickCol(projCols, [/^dk\s*proj/i, /\bdk\b.*proj/i]),
    [projCols]
  );
  const fdProjCol = useMemo(
    () => pickCol(projCols, [/^fd\s*proj/i, /\bfd\b.*proj/i]),
    [projCols]
  );

  function getProjRow(row) {
    const name = getDriverName(row) || "";
    return (
      projByDriver.get(normalizeDriverKey(name, { stripSuffix: false })) ||
      projByDriver.get(normalizeDriverKey(name, { stripSuffix: true })) ||
      null
    );
  }

  function numLogoSrc(carNum) {
    if (carNum == null) return "";
    const seriesFolder = key === "xfinity" ? "Xfinity" : key === "trucks" ? "Trucks" : "cup";
    return `${NUM_LOGO_BASE}/${seriesFolder}/${Math.round(carNum)}.png`;
  }

  function makeLogoSrc(makeVal) {
    const slug0 = lower(String(makeVal || "")).replace(/[^a-z0-9]+/g, "");
    const slug = slug0 === "chevy" || slug0 === "chev" ? "chevrolet" : slug0;
    return slug ? `${MAKE_LOGO_BASE}/${slug}.png` : "";
  }

  function getCarNum(row) {
    const n = carNumColName ? parseNumericLike(row?.[carNumColName]) : null;
    return n != null ? Math.round(n) : null;
  }

  function getMake(row) {
    for (const c of rawCols) {
      const kn = keynorm(stripDupSuffix(c));
      if (carMakeNorms.has(kn)) return row?.[c];
    }
    return "";
  }

  function getTeam(row) {
    return teamCol ? row?.[teamCol] : row?.Team || "";
  }

  function getProj(row) {
    const pr = getProjRow(row);
    return {
      dk: pr && dkProjCol ? parseNumericLike(pr[dkProjCol]) : null,
      fd: pr && fdProjCol ? parseNumericLike(pr[fdProjCol]) : null,
    };
  }

  function moneyLike(v) {
    const n = parseNumericLike(v);
    return Number.isFinite(n)
      ? n.toLocaleString(undefined, { maximumFractionDigits: 0 })
      : "—";
  }
  function oneDec(v) {
    return Number.isFinite(v) ? v.toFixed(1) : "—";
  }

  function getTeamLogoSrc(teamName) {
    return `${BASE}logos/nascar/teams/${slugify(teamName)}.png`;
  }

  const [profileSort, setProfileSort] = useState({ key: "radar", dir: "desc" });
  const [profileDisplay, setProfileDisplay] = useState("cards");
  const [profileTabByDriver, setProfileTabByDriver] = useState({});

  const availableYears = useMemo(() => {
    const wantedSeries = seriesLabelFromKey(key);
    const set = new Set();
    for (const r of raceLogModalRows || []) {
      if (!seriesMatches(r?.series, wantedSeries)) continue;
      const y = safeYearFromDate(r?.race_date);
      if (y) set.add(Number(y));
    }
    return Array.from(set).sort((a, b) => b - a);
  }, [raceLogModalRows, key]);

  const [selectedProfileYears, setSelectedProfileYears] = useState([]);
  const [selectedProfileTrackType, setSelectedProfileTrackType] = useState("all");

  useEffect(() => {
    setSelectedProfileYears([]);
    setSelectedProfileTrackType("all");
  }, [key]);

  function toggleProfileYear(year) {
    setSelectedProfileYears((prev) =>
      prev.includes(year)
        ? prev.filter((y) => y !== year)
        : [...prev, year].sort((a, b) => b - a)
    );
  }

  const profileRows = useMemo(() => {
    const seriesLabel = seriesLabelFromKey(key);

    const currentDrivers = [...sorted]
      .map((row) => ({ row, driver: getDriverName(row) }))
      .filter((x) => x.driver);

    const aggByDriver = new Map();

    for (const { driver } of currentDrivers) {
      aggByDriver.set(
        driver,
        aggregateDriverTrackTypeStats(
          raceLogModalRows,
          driver,
          seriesLabel,
          selectedProfileYears
        )
      );
    }

      const pools = Object.fromEntries(
        PROFILE_TRACK_TYPES.map((type) => [
          type,
          {
            avgFinish: [],
            avgRun: [],
            avgRating: [],
            avgGfsRank: [],
            avgPassingDiff: [],
            avgQualityPassPct: [],
            avgTop15LapPct: [],
            avgLapsLedPct: [],
            winRate: [],
            top5Rate: [],
            top10Rate: [],
          },
        ])
      );

    for (const agg of aggByDriver.values()) {
      for (const type of PROFILE_TRACK_TYPES) {
        const s = agg[type];
        if (!s || !s.races) continue;
        if (Number.isFinite(s.avgFinish)) pools[type].avgFinish.push(s.avgFinish);
        if (Number.isFinite(s.avgRun)) pools[type].avgRun.push(s.avgRun);
        if (Number.isFinite(s.avgRating)) pools[type].avgRating.push(s.avgRating);
        if (Number.isFinite(s.avgGfsRank)) pools[type].avgGfsRank.push(s.avgGfsRank);
        if (Number.isFinite(s.avgPassingDiff)) pools[type].avgPassingDiff.push(s.avgPassingDiff);
        if (Number.isFinite(s.avgQualityPassPct)) pools[type].avgQualityPassPct.push(s.avgQualityPassPct);
        if (Number.isFinite(s.avgTop15LapPct)) pools[type].avgTop15LapPct.push(s.avgTop15LapPct);
        if (Number.isFinite(s.avgLapsLedPct)) pools[type].avgLapsLedPct.push(s.avgLapsLedPct);
        if (Number.isFinite(s.winRate)) pools[type].winRate.push(s.winRate);
        if (Number.isFinite(s.top5Rate)) pools[type].top5Rate.push(s.top5Rate);
        if (Number.isFinite(s.top10Rate)) pools[type].top10Rate.push(s.top10Rate);
      }
    }

    const out = currentDrivers.map(({ row, driver }) => {
      const agg = aggByDriver.get(driver) || {};

      const radar = PROFILE_TRACK_TYPES.map((type) => {
        const s = agg[type];
        const shortLabel = prettyTrackType(type)
          .replace("Superspeedways", "SS")
          .replace("Road Courses", "Road")
          .replace("Intermediates", "Intermediate")
          .replace("Short Tracks", "Short")
          .replace("Short Flat", "Short/Flat")
          .replace("Large Ovals", "Large Track")

        if (!s || !s.races) {
          return {
            key: type,
            label: prettyTrackType(type),
            shortLabel,
            value: null,
            races: 0,
            avgFinish: null,
            avgRun: null,
            avgRating: null,
            wins: 0,
            top10: 0,
          };
        }

        const gfsRankPct = percentileRank(pools[type].avgGfsRank, s.avgGfsRank, false);
        const finishPct = percentileRank(pools[type].avgFinish, s.avgFinish, false);
        const runPct = percentileRank(pools[type].avgRun, s.avgRun, false);
        const ratingPct = percentileRank(pools[type].avgRating, s.avgRating, true);
        const passingDiffPct = percentileRank(pools[type].avgPassingDiff, s.avgPassingDiff, true);
        const qualityPassPct = percentileRank(pools[type].avgQualityPassPct, s.avgQualityPassPct, true);
        const top15LapPct = percentileRank(pools[type].avgTop15LapPct, s.avgTop15LapPct, true);
        const lapsLedPct = percentileRank(pools[type].avgLapsLedPct, s.avgLapsLedPct, true);
        const top5RatePct = percentileRank(pools[type].top5Rate, s.top5Rate, true);
        const top10RatePct = percentileRank(pools[type].top10Rate, s.top10Rate, true);

        const rawValue = weightedAverage([
          { value: gfsRankPct, weight: 0.28 },
          { value: finishPct, weight: 0.20 },
          { value: runPct, weight: 0.18 },
          { value: ratingPct, weight: 0.16 },
          { value: top15LapPct, weight: 0.08 },
          { value: passingDiffPct, weight: 0.05 },
          { value: qualityPassPct, weight: 0.03 },
          { value: lapsLedPct, weight: 0.01 },
          { value: top5RatePct, weight: 0.005 },
          { value: top10RatePct, weight: 0.005 },
        ]);

        const sampleFactor = Math.min(1, (s.races || 0) / 4);
        const value = Number.isFinite(rawValue) ? rawValue * sampleFactor : null;

      return {
        key: type,
        label: prettyTrackType(type),
        shortLabel,
        value,
        races: s.races,
        avgFinish: s.avgFinish,
        avgRun: s.avgRun,
        avgRating: s.avgRating,
        avgGfsRank: s.avgGfsRank,
        avgPassingDiff: s.avgPassingDiff,
        avgQualityPassPct: s.avgQualityPassPct,
        avgTop15LapPct: s.avgTop15LapPct,
        avgLapsLedPct: s.avgLapsLedPct,
        winRate: s.winRate,
        top5Rate: s.top5Rate,
        top10Rate: s.top10Rate,
        wins: s.wins,
        top5: s.top5,
        top10: s.top10,
      };
      });

      const validRadarValues = radar.map((r) => r.value).filter(Number.isFinite);
      const radarOverall = average(validRadarValues);
      const selectedTrackTypeProfile =
        selectedProfileTrackType === "all"
          ? radarOverall
          : radar.find((r) => r.key === selectedProfileTrackType)?.value ?? null;

      const validGfsRanks = radar.map((r) => r.avgGfsRank).filter(Number.isFinite);
      const avgGfsRankOverall = average(validGfsRanks);
      const selectedAvgGfsRank =
        selectedProfileTrackType === "all"
          ? avgGfsRankOverall
          : radar.find((r) => r.key === selectedProfileTrackType)?.avgGfsRank ?? null;

      const bestTypes = [...radar]
        .filter((r) => Number.isFinite(r.value))
        .sort((a, b) => b.value - a.value)
        .slice(0, 3);

      const worstTypes = [...radar]
        .filter((r) => Number.isFinite(r.value))
        .sort((a, b) => a.value - b.value)
        .slice(0, 2);

      const bestTypeValue = bestTypes.length ? bestTypes[0].value : null;

      const categoryEdges = radar
        .filter((r) => Number.isFinite(r.value))
        .map((r) => ({
          ...r,
          vsProfileAvgPct: safePctDiff(r.value, radarOverall),
          vsBestPct: safePctDiff(r.value, bestTypeValue),
        }))
        .sort((a, b) => (b.value ?? -1e9) - (a.value ?? -1e9));

      const carNum = getCarNum(row);
      const makeVal = getMake(row);
      const team = getTeam(row);
      const qual = qualCol ? parseNumericLike(row?.[qualCol]) : null;
      const odds = oddsCol ? parseNumericLike(row?.[oddsCol]) : null;
      const prac = practiceAvg(row);
      const proj = getProj(row);
      const dkSal = dkSalCol ? row?.[dkSalCol] : null;
      const fdSal = fdSalCol ? row?.[fdSalCol] : null;

      return {
        row,
        driver,
        team,
        carNum,
        makeVal,
        qual,
        odds,
        prac,
        dkSal,
        fdSal,
        proj,
        radar,
        radarOverall,
        selectedTrackTypeProfile,
        avgGfsRankOverall,
        selectedAvgGfsRank,
        bestTypes,
        worstTypes,
        categoryEdges,
        numImg: carNum != null ? numLogoSrc(carNum) : "",
        makeImg: makeLogoSrc(makeVal),
        teamImg: getTeamLogoSrc(team),
      };
    });

    const sgn = profileSort.dir === "asc" ? 1 : -1;

    out.sort((a, b) => {
      if (profileSort.key === "radar") {
        return sgn * ((a.selectedTrackTypeProfile ?? -1e9) - (b.selectedTrackTypeProfile ?? -1e9));
      }
      if (profileSort.key === "dkproj") {
        return sgn * ((a.proj?.dk ?? -1e9) - (b.proj?.dk ?? -1e9));
      }
      if (profileSort.key === "fdproj") {
        return sgn * ((a.proj?.fd ?? -1e9) - (b.proj?.fd ?? -1e9));
      }
      if (profileSort.key === "qual") {
        return sgn * ((a.qual ?? 1e9) - (b.qual ?? 1e9));
      }
      if (profileSort.key === "practice") {
        return sgn * ((a.prac ?? 1e9) - (b.prac ?? 1e9));
      }
      if (profileSort.key === "name") {
        return (
          sgn *
          String(a.driver || "").localeCompare(String(b.driver || ""), undefined, {
            sensitivity: "base",
          })
        );
      }
      return 0;
    });

    return out;
  }, [
    sorted,
    raceLogModalRows,
    key,
    profileSort,
    qualCol,
    oddsCol,
    dkSalCol,
    fdSalCol,
    teamCol,
    dkProjCol,
    fdProjCol,
    projByDriver,
    rawCols,
    carNumColName,
    carMakeNorms,
    practiceCols,
    selectedProfileYears,
    selectedProfileTrackType,
  ]);

  const pillWrap =
    "inline-flex items-center rounded-full px-1 py-1 text-[11px] ring-1 " +
    (isDark ? "bg-slate-900 ring-slate-700/80" : "bg-white ring-slate-300");

  const pillBtn = (active) =>
    "px-3 py-1 rounded-full font-medium transition " +
    (active
      ? "bg-sky-500 text-slate-950 shadow-sm"
      : isDark
      ? "text-slate-400 hover:text-slate-100"
      : "text-slate-500 hover:text-slate-800");

  const viewPillBtn = (active) =>
    "border-b-2 px-3 py-2.5 text-xs font-semibold transition " +
    (active
      ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900")
      : isDark
      ? "border-transparent text-slate-400 hover:text-white"
      : "border-transparent text-slate-500 hover:text-slate-900");

  return (
    <div className={T.page}>
      <div className="mx-auto w-full px-4 md:px-6 py-5">
        <header className="mb-3 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h1 className="text-xl font-bold tracking-tight">{title}</h1>
              <span className={"text-xs font-normal italic " + (isDark ? "text-slate-100" : "text-slate-700")}>
                Updated: {updatedAt ? fmtUpdated(updatedAt) : "—"}
              </span>
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-2">
              {view === "table" && (
            <button
              type="button"
              onClick={() => { setQ(""); setSelectedTeam(""); setSelectedManufacturer(""); }}
              className={
                "h-8 shrink-0 rounded-md border px-3 text-xs transition " +
                (isDark ? "border-slate-700 bg-slate-950 text-slate-100 hover:bg-slate-900" : "border-slate-300 bg-white text-slate-900 hover:bg-slate-50")
              }
            >Reset</button>
              )}
              <div className={pillWrap}>
                <button type="button" onClick={() => setTheme("dark")} className={pillBtn(isDark)}>Dark</button>
                <button type="button" onClick={() => setTheme("light")} className={pillBtn(!isDark)}>Light</button>
              </div>
            </div>
          </div>

          <div className={"flex flex-wrap items-center justify-between gap-x-6 gap-y-2 rounded-2xl border px-3 py-1 shadow-sm " + (isDark ? "border-slate-800 bg-slate-900/95" : "border-slate-200 bg-white")}>
            <nav aria-label="NASCAR series" className={"flex items-center gap-5 " +
              (isDark ? "border-slate-800" : "border-slate-200")}>
              {[
                ["cup", "Cup"],
                ["xfinity", "O'Reilly"],
                ["trucks", "Trucks"],
              ].map(([k, label]) => (
                <a
                  key={k}
                  href={`${BASE}nascar/${k}/data`}
                  aria-current={key === k ? "page" : undefined}
                  className={
                    "relative whitespace-nowrap px-1 py-2.5 text-xs font-semibold transition-colors " +
                    (key === k
                      ? (isDark ? "text-white" : "text-slate-950") + " after:absolute after:bottom-[-1px] after:left-0 after:right-0 after:h-[2px] after:rounded-full after:bg-sky-400"
                      : isDark ? "text-slate-400 hover:text-slate-100" : "text-slate-500 hover:text-slate-900")
                  }
                >{label}</a>
              ))}
            </nav>

            <div className="flex min-w-0 flex-wrap items-center gap-3 pb-1">
              <div className="min-w-0 max-w-full overflow-x-auto">
                <div className="inline-flex flex-wrap items-center gap-1">
                  <button type="button" onClick={() => setView("table")} className={viewPillBtn(view === "table")}>Table</button>
                  <button type="button" onClick={() => setView("charts")} className={viewPillBtn(view === "charts")}>Charts</button>
                  <button type="button" onClick={() => setView("profiles")} className={viewPillBtn(view === "profiles")}>Driver Profiles</button>
                  <button type="button" onClick={() => setView("ratings")} className={viewPillBtn(view === "ratings")}>Ratings</button>
                </div>
              </div>

            </div>
          </div>
        </header>

        {view === "table" && (
          <div className={`mb-4 flex flex-wrap items-center gap-2 rounded-2xl ${T.card} p-3 xl:flex-nowrap`}>
            <input
              type="search"
              aria-label="Search driver, team, or make"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search driver / team / make…"
              className={
                "h-8 w-full min-w-0 rounded-md border px-3 text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 sm:flex-[1_1_240px] xl:flex-[1_1_240px] " +
                (isDark
                  ? "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500"
                  : "bg-white border-slate-300 text-slate-800 placeholder:text-slate-400")
              }
            />
            <select
              aria-label="Filter by team"
              value={selectedTeam}
              onChange={(e) => setSelectedTeam(e.target.value)}
              className={
                "h-8 min-w-0 flex-1 rounded-md border px-3 text-xs sm:flex-[0_1_210px] xl:w-[210px] xl:flex-none " +
                (isDark ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300 bg-white text-slate-900")
              }
            >
              <option value="">All teams</option>
              {teamOptions.map((team) => <option key={team} value={team}>{team}</option>)}
            </select>
            <select
              aria-label="Filter by manufacturer"
              value={selectedManufacturer}
              onChange={(e) => setSelectedManufacturer(e.target.value)}
              className={
                "h-8 min-w-0 flex-1 rounded-md border px-3 text-xs sm:flex-[0_1_190px] xl:w-[190px] xl:flex-none " +
                (isDark ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300 bg-white text-slate-900")
              }
            >
              <option value="">All manufacturers</option>
              {manufacturerOptions.map((make) => <option key={make} value={make}>{make}</option>)}
            </select>

          </div>
        )}

        {view === "table" && (
          <div className={`${T.radius} ${T.card} overflow-auto max-h-[66vh] overscroll-contain`}>
            <table className={`w-full border-separate ${textSz}`} style={{ borderSpacing: 0 }}>
              {columns.length > 0 && (
                <thead>
                  <tr className={`${T.bandBg} text-[11px] font-bold uppercase ${T.text}`}>
                    {bands.map((g, gi) => (
                      <th
                        key={`${g.name}-${g.start}`}
                        colSpan={g.span}
                        className={`px-2 py-1 text-center border-b ${T.bandBorder} ${
                          gi === 0 ? "first:rounded-tl-2xl" : ""
                        } ${gi === bands.length - 1 ? "last:rounded-tr-2xl" : ""}`}
                      >
                        {g.name}
                      </th>
                    ))}
                  </tr>

                  <tr className={`${T.headerBg} sticky top-0 z-30`}>
                    {columns.map((c, i) => {
                      const kn = keynorm(stripDupSuffix(c));
                      const isDriver = kn === "driver" || kn === "name";
                      const thickCut = bandCuts.has(i);
                      const borderR = thickCut ? `border-r-2 ${T.bandBorder}` : `border-r ${T.border}`;
                      const borderL =
                        overallIndex != null && i === overallIndex ? `border-l-2 ${T.bandBorder}` : "";

                      return (
                        <th
                          key={c}
                          onClick={() => onSort(c)}
                          title="Click to sort"
                          className={[
                            headerCell,
                            borderR,
                            borderL,
                            isDriver ? `text-left sticky left-0 z-40 ${T.headerBg}` : "",
                          ].join(" ")}
                        >
                          <span className="inline-flex items-center gap-1">
                            <span>{stripDupSuffix(kn === "overall" ? "Overall" : c)}</span>
                            {sort.key === c ? (
                              <span className="text-slate-400">{sort.dir === "desc" ? "▼" : "▲"}</span>
                            ) : (
                              <span className="text-slate-500/30">▲</span>
                            )}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
              )}

              <tbody>
                {loading && (
                  <tr>
                    <td className={`${cellCls} ${T.subtext}`} colSpan={columns.length}>
                      Loading…
                    </td>
                  </tr>
                )}
                {err && (
                  <tr>
                    <td className={`${cellCls} text-red-400`} colSpan={columns.length}>
                      Failed to load: {err}
                    </td>
                  </tr>
                )}

                {!loading &&
                  !err &&
                  sorted.map((row, rIdx) => {
                    const rowBg = rIdx % 2 ? T.zebraEven : T.zebraOdd;

                    return (
                      <tr
                        key={`${getDriverName(row) || "row"}-${rIdx}`}
                        className={`${rowBg} group ${isDark ? "hover:bg-slate-800/70" : "hover:bg-slate-200"} transition-colors`}
                      >
                        {columns.map((c, i) => {
                          const nice = stripDupSuffix(c);
                          const kn = keynorm(nice);
                          const isDriver = kn === "driver" || kn === "name";
                          const isCarNumCol = carNumColName && kn === carNumNorm;
                          const isCarMakeCol = carMakeNorms.has(kn);
                          const raw = row[c];

                          const thickCut = bandCuts.has(i);
                          const borders =
                            (overallIndex != null && i === overallIndex ? `border-l-2 ${T.bandBorder} ` : "") +
                            (thickCut ? `border-r-2 ${T.bandBorder}` : `border-r ${T.border}`);

                          if (isDriver) {
                            return (
                              <td
                                key={`${c}-${rIdx}`}
                                className={[
                                  cellCls,
                                  "text-left font-medium",
                                  borders,
                                  "sticky left-0 z-20 group-hover:z-50",
                                  driverMinWidth,
                                  rowBg,
                                  "overflow-visible",
                                  "shadow-[inset_-6px_0_6px_-6px_rgba(0,0,0,0.35)]",
                                ].join(" ")}
                                title={String(raw ?? "")}
                              >
                                {(() => {
                                  if (isDriverColumnName(c)) {
                                    const dn = String(raw ?? "").trim();
                                    if (!dn) return fmtCell(raw);
                                    return (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setRaceLogDriver(dn);
                                          setRaceLogOpen(true);
                                        }}
                                        className={`relative group/driver inline-flex items-center text-left font-semibold cursor-pointer decoration-2 underline-offset-4 no-underline group-hover/driver:underline transition-colors duration-150 ${
                                          isDark
                                            ? "text-slate-200 group-hover/driver:text-white"
                                            : "text-slate-900 group-hover/driver:text-black"
                                        }`}
                                      >
                                        {dn}
                                        <span
                                          className={`pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 whitespace-nowrap rounded-md px-2 py-1 text-[10px] font-semibold z-[80] opacity-0 translate-y-1 scale-95 transition-all duration-150 ease-out group-hover/driver:opacity-100 group-hover/driver:translate-y-0 group-hover/driver:scale-100 ${
                                            isDark
                                              ? "bg-slate-800 text-slate-100 shadow-lg"
                                              : "bg-slate-900 text-white shadow-md"
                                          }`}
                                        >
                                          Click to open race log
                                        </span>
                                      </button>
                                    );
                                  }
                                  return fmtCell(raw);
                                })()}
                              </td>
                            );
                          }

                          const stat = heatStats[c];
                          const textColor = stat
                            ? heatTextColor(stat.min, stat.max, raw, stat.dir, theme)
                            : null;

                          if (isCarNumCol && (key === "cup" || key === "xfinity" || key === "trucks")) {
                            const n = parseNumericLike(row[c]);
                            const seriesFolder =
                              key === "xfinity" ? "Xfinity" : key === "trucks" ? "Trucks" : "cup";
                            const imgSrc =
                              n != null ? `${NUM_LOGO_BASE}/${seriesFolder}/${Math.round(n)}.png` : "";

                            return (
                              <td key={`${c}-${rIdx}`} className={`text-center ${cellCls} ${borders}`}>
                                <span className="inline-flex items-center justify-center">
                                  <img
                                    src={imgSrc}
                                    alt={n != null ? `Car ${Math.round(n)}` : "Car number"}
                                    className="h-5 w-5 object-contain transition-transform duration-150 group-hover:scale-105"
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                      if (e.currentTarget.nextSibling)
                                        e.currentTarget.nextSibling.style.display = "inline";
                                    }}
                                  />
                                  <span
                                    style={
                                      textColor
                                        ? { display: "none", color: textColor, fontWeight: 600 }
                                        : { display: "none" }
                                    }
                                  >
                                    {fmtCell(row[c])}
                                  </span>
                                </span>
                              </td>
                            );
                          }

                          if (isCarMakeCol) {
                            const slug0 = lower(String(row[c] || "")).replace(/[^a-z0-9]+/g, "");
                            const slug = slug0 === "chevy" || slug0 === "chev" ? "chevrolet" : slug0;
                            const imgSrc = slug ? `${MAKE_LOGO_BASE}/${slug}.png` : "";
                            const makeName = String(row[c] || "").trim();

                            return (
                              <td key={`${c}-${rIdx}`} className={`text-center ${cellCls} ${borders}`}>
                                <span className="inline-flex items-center justify-center">
                                  <img
                                    src={imgSrc}
                                    alt={makeName ? `${makeName} logo` : "Manufacturer logo"}
                                    className="h-5 w-5"
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                      if (e.currentTarget.nextSibling)
                                        e.currentTarget.nextSibling.style.display = "inline";
                                    }}
                                  />
                                  <span
                                    style={
                                      textColor
                                        ? { display: "none", color: textColor, fontWeight: 600 }
                                        : { display: "none" }
                                    }
                                  >
                                    {fmtCell(row[c])}
                                  </span>
                                </span>
                              </td>
                            );
                          }

                          return (
                            <td
                              key={`${c}-${rIdx}`}
                              className={[cellCls, "text-center", borders, "tabular-nums"].join(" ")}
                              title={String(raw ?? "")}
                            >
                              <span
                                style={textColor ? { color: textColor, fontWeight: 600 } : undefined}
                              >
                                {fmtCell(raw)}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}

                {!loading && !err && sorted.length === 0 && (
                  <tr>
                    <td className={`${cellCls} ${T.subtext}`} colSpan={columns.length}>
                      No rows match your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {view === "charts" && (
          <div className={`${T.radius} ${T.card} p-4 md:p-5`}>
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <div>
                  <div className={"text-xs mb-1 " + (isDark ? "text-slate-400" : "text-slate-600")}>
                    Section
                  </div>
                  <select
                    value={chartBand}
                    onChange={(e) => setChartBand(e.target.value)}
                    className={
                      "h-9 rounded-full border px-3 text-sm focus:outline-none " +
                      (isDark
                        ? "bg-slate-950/80 border-slate-700 text-slate-100"
                        : "bg-white border-slate-300 text-slate-800")
                    }
                  >
                    {chartBands.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className={"text-xs mb-1 " + (isDark ? "text-slate-400" : "text-slate-600")}>
                    X Axis
                  </div>
                  <select
                    value={xCol}
                    onChange={(e) => setXCol(e.target.value)}
                    className={
                      "h-9 rounded-full border px-3 text-sm focus:outline-none " +
                      (isDark
                        ? "bg-slate-950/80 border-slate-700 text-slate-100"
                        : "bg-white border-slate-300 text-slate-800")
                    }
                  >
                    {chartCols.map((c) => (
                      <option key={c} value={c}>
                        {stripDupSuffix(c)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className={"text-xs mb-1 " + (isDark ? "text-slate-400" : "text-slate-600")}>
                    Y Axis
                  </div>
                  <select
                    value={yCol}
                    onChange={(e) => setYCol(e.target.value)}
                    className={
                      "h-9 rounded-full border px-3 text-sm focus:outline-none " +
                      (isDark
                        ? "bg-slate-950/80 border-slate-700 text-slate-100"
                        : "bg-white border-slate-300 text-slate-800")
                    }
                  >
                    {chartCols.map((c) => (
                      <option key={c} value={c}>
                        {stripDupSuffix(c)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end gap-2">
                  <label className={"text-xs " + (isDark ? "text-slate-400" : "text-slate-600")}>
                    Reverse X
                    <input
                      type="checkbox"
                      className="ml-2 align-middle"
                      checked={isRevX}
                      onChange={(e) => setRevX(e.target.checked)}
                    />
                  </label>
                  <label className={"text-xs " + (isDark ? "text-slate-400" : "text-slate-600")}>
                    Reverse Y
                    <input
                      type="checkbox"
                      className="ml-2 align-middle"
                      checked={isRevY}
                      onChange={(e) => setRevY(e.target.checked)}
                    />
                  </label>
                </div>
              </div>

              <div className={"text-xs " + (isDark ? "text-slate-400" : "text-slate-600")}>
                {chartPoints.length ? (
                  <span>
                    Plotting{" "}
                    <span className={isDark ? "text-slate-200" : "text-slate-800"}>
                      {chartPoints.length}
                    </span>{" "}
                    drivers • Avg lines shown
                  </span>
                ) : (
                  <span>Pick stats to plot</span>
                )}
              </div>
            </div>

            <div className="mt-4 w-full min-w-0">
              <div className={`mx-auto w-full max-w-[1120px] lg:w-[85%] ${T.radius} ${isDark ? "bg-slate-900" : "bg-white"} border ${T.border} p-3`}>
                {(!plot || !chartStats) && (
                  <div className={isDark ? "text-slate-400" : "text-slate-600"}>No chart data.</div>
                )}

                {plot && chartStats && (
                  <svg
                    width="100%"
                    height="auto"
                    viewBox={`0 0 ${plot.W} ${plot.H}`}
                    className="block h-auto w-full"
                  >
                    <rect
                      x="0"
                      y="0"
                      width={plot.W}
                      height={plot.H}
                      rx="16"
                      ry="16"
                      fill={isDark ? "#0f172a" : "#ffffff"}
                    />

                    <line
                      x1={plot.avgX}
                      x2={plot.avgX}
                      y1={plot.pad.t}
                      y2={plot.pad.t + plot.innerH}
                      stroke={isDark ? "rgba(56,189,248,0.45)" : "rgba(2,132,199,0.35)"}
                      strokeDasharray="6 6"
                    />
                    <line
                      x1={plot.pad.l}
                      x2={plot.pad.l + plot.innerW}
                      y1={plot.avgY}
                      y2={plot.avgY}
                      stroke={isDark ? "rgba(59,130,246,0.45)" : "rgba(37,99,235,0.30)"}
                      strokeDasharray="6 6"
                    />

                    <line
                      x1={plot.pad.l}
                      x2={plot.pad.l + plot.innerW}
                      y1={plot.pad.t + plot.innerH}
                      y2={plot.pad.t + plot.innerH}
                      stroke={isDark ? "rgba(148,163,184,0.35)" : "rgba(15,23,42,0.25)"}
                    />
                    <line
                      x1={plot.pad.l}
                      x2={plot.pad.l}
                      y1={plot.pad.t}
                      y2={plot.pad.t + plot.innerH}
                      stroke={isDark ? "rgba(148,163,184,0.35)" : "rgba(15,23,42,0.25)"}
                    />

                    <text
                      x={plot.pad.l + plot.innerW / 2}
                      y={plot.H - 18}
                      textAnchor="middle"
                      fontSize="12"
                      fill={isDark ? "rgba(226,232,240,0.9)" : "rgba(15,23,42,0.75)"}
                    >
                      {stripDupSuffix(xCol)}
                    </text>
                    <text
                      x="18"
                      y={plot.pad.t + plot.innerH / 2}
                      textAnchor="middle"
                      fontSize="12"
                      fill={isDark ? "rgba(226,232,240,0.9)" : "rgba(15,23,42,0.75)"}
                      transform={`rotate(-90 18 ${plot.pad.t + plot.innerH / 2})`}
                    >
                      {stripDupSuffix(yCol)}
                    </text>

                    {plot.points.map((p) => {
                      const img = p.carNum != null ? numLogoSrc(p.carNum) : "";
                      return (
                        <g key={p.id} transform={`translate(${p.x},${p.y})`}>
                          <circle
                            cx="0"
                            cy="0"
                            r="16"
                            fill={isDark ? "rgba(2,6,23,0.35)" : "rgba(255,255,255,0.7)"}
                            stroke={isDark ? "rgba(148,163,184,0.25)" : "rgba(15,23,42,0.18)"}
                          />
                          {img ? (
                            <image
                              href={img}
                              x={-14}
                              y={-14}
                              width={28}
                              height={28}
                              preserveAspectRatio="xMidYMid meet"
                            />
                          ) : (
                            <text
                              x="0"
                              y="4"
                              textAnchor="middle"
                              fontSize="10"
                              fill={isDark ? "rgba(226,232,240,0.95)" : "rgba(15,23,42,0.85)"}
                            >
                              {p.driver?.slice(0, 2) || "•"}
                            </text>
                          )}
                          <title>
                            {p.driver}
                            {"\n"}
                            {stripDupSuffix(xCol)}: {p.xVal}
                            {"\n"}
                            {stripDupSuffix(yCol)}: {p.yVal}
                          </title>
                        </g>
                      );
                    })}
                  </svg>
                )}
              </div>
            </div>
          </div>
        )}

        {view === "ratings" && (
          <DriverRatingsTable
            profileRows={!loading && !err ? profileRows : []}
            T={T}
            isDark={isDark}
            getNumLogoSrc={numLogoSrc}
            availableYears={availableYears}
            selectedYears={selectedProfileYears}
            onToggleYear={toggleProfileYear}
            onAllYears={() => setSelectedProfileYears([])}
          />
        )}

        {view === "profiles" && (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <div className={"text-xs " + (isDark ? "text-slate-400" : "text-slate-600")}>Sort</div>

              <select
                value={`${profileSort.key}:${profileSort.dir}`}
                onChange={(e) => {
                  const [k, d] = String(e.target.value).split(":");
                  setProfileSort({ key: k, dir: d });
                }}
                className={
                  "h-9 rounded-full border px-3 text-sm focus:outline-none " +
                  (isDark
                    ? "bg-slate-950/80 border-slate-700 text-slate-100"
                    : "bg-white border-slate-300 text-slate-800")
                }
              >
                <option value="radar:desc">Track Type Profile (best)</option>
                <option value="dkproj:desc">DK Proj</option>
                <option value="fdproj:desc">FD Proj</option>
                <option value="practice:asc">Practice Avg (best)</option>
                <option value="qual:asc">Qual (best)</option>
                <option value="name:asc">Name (A-Z)</option>
              </select>

              <select
                value={selectedProfileTrackType}
                onChange={(e) => setSelectedProfileTrackType(e.target.value)}
                className={
                  "h-9 rounded-full border px-3 text-sm focus:outline-none " +
                  (isDark
                    ? "bg-slate-950/80 border-slate-700 text-slate-100"
                    : "bg-white border-slate-300 text-slate-800")
                }
              >
                <option value="all">All Track Types</option>
                {PROFILE_TRACK_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {prettyTrackType(type)}
                  </option>
                ))}
              </select>

              <div className="flex items-center gap-1 rounded-full border border-slate-700/70 bg-slate-950/60 p-1">
                <button
                  type="button"
                  onClick={() => setProfileDisplay("cards")}
                  className={
                    "px-3 py-1.5 rounded-full text-xs font-semibold transition " +
                    (profileDisplay === "cards"
                      ? "bg-sky-500 text-slate-950 shadow-sm"
                      : isDark
                      ? "text-slate-300 hover:text-white"
                      : "text-slate-700 hover:text-slate-900")
                  }
                >
                  Cards
                </button>
                <button
                  type="button"
                  onClick={() => setProfileDisplay("sheet")}
                  className={
                    "px-3 py-1.5 rounded-full text-xs font-semibold transition " +
                    (profileDisplay === "sheet"
                      ? "bg-sky-500 text-slate-950 shadow-sm"
                      : isDark
                      ? "text-slate-300 hover:text-white"
                      : "text-slate-700 hover:text-slate-900")
                  }
                >
                  Sheet
                </button>
              </div>

              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search drivers..."
                className={
                  "h-9 w-56 md:w-96 rounded-full border px-4 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 " +
                  (isDark
                    ? "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500"
                    : "bg-white border-slate-300 text-slate-800 placeholder:text-slate-400")
                }
              />

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedProfileYears([])}
                  className={
                    "h-9 rounded-full border px-3 text-sm font-semibold transition " +
                    (selectedProfileYears.length === 0
                      ? "bg-sky-500 text-slate-950 border-sky-400"
                      : isDark
                      ? "bg-slate-950/80 border-slate-700 text-slate-200 hover:bg-slate-900"
                      : "bg-white border-slate-300 text-slate-800 hover:bg-slate-50")
                  }
                >
                  All Years
                </button>

                {availableYears.map((yr) => {
                  const active = selectedProfileYears.includes(yr);
                  return (
                    <button
                      key={yr}
                      type="button"
                      onClick={() => toggleProfileYear(yr)}
                      className={
                        "h-9 rounded-full border px-3 text-sm font-semibold transition " +
                        (active
                          ? "bg-sky-500 text-slate-950 border-sky-400"
                          : isDark
                          ? "bg-slate-950/80 border-slate-700 text-slate-200 hover:bg-slate-900"
                          : "bg-white border-slate-300 text-slate-800 hover:bg-slate-50")
                      }
                    >
                      {yr}
                    </button>
                  );
                })}
              </div>

              {(projLoading || projErr) && (
                <div className={"text-xs " + (isDark ? "text-slate-400" : "text-slate-600")}>
                  {projLoading ? "Loading projections…" : "Projections failed to load"}
                </div>
              )}
            </div>

            {profileDisplay === "cards" && (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {(!loading && !err ? profileRows : []).map((p, idx) => {
                const tabKey = normalizeDriverKey(p.driver, { stripSuffix: false });
                const activeProfileTab = profileTabByDriver[tabKey] || "radar";

                return (
                  <div
                    key={`${p.driver}-${idx}`}
                    className={[
                      T.radius,
                      "border",
                      T.border,
                      "p-4",
                      isDark ? "bg-slate-950/90" : "bg-white",
                      "shadow-[0_18px_50px_rgba(2,6,23,0.45)]",
                    ].join(" ")}
                  >
                    <div className="flex flex-col 2xl:flex-row gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-4">
                          <div className="w-20 h-20 rounded-2xl overflow-hidden border border-slate-700 bg-slate-900 flex items-center justify-center shrink-0">
                            <DriverHeadshot
                              name={p.driver}
                              initials={p.driver.slice(0, 2).toUpperCase()}
                              className="w-full h-full object-contain object-center"
                              fallbackClassName="w-full h-full flex items-center justify-center text-lg font-extrabold text-slate-100"
                            />
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-3">
                              {p.numImg ? (
                                <img
                                  src={p.numImg}
                                  alt={`Car ${p.carNum}`}
                                  className="h-10 w-10 shrink-0 object-contain"
                                  onError={(e) => (e.currentTarget.style.display = "none")}
                                />
                              ) : null}
                              <div className="min-w-0">
                                <div className="text-2xl font-extrabold tracking-tight truncate">{p.driver}</div>
                                <div className={isDark ? "text-slate-300 truncate" : "text-slate-600 truncate"}>
                                  {p.team || "—"}
                                </div>
                              </div>
                            </div>

                            <div className="mt-2 flex flex-wrap items-center gap-3">
                              {p.makeImg ? (
                                <img
                                  src={p.makeImg}
                                  alt={p.makeVal || "Make"}
                                  className="h-6 w-6"
                                  onError={(e) => (e.currentTarget.style.display = "none")}
                                />
                              ) : null}

                              {p.teamImg ? (
                                <img
                                  src={p.teamImg}
                                  alt={p.team || "Team"}
                                  className="h-6 w-auto max-w-[90px] object-contain"
                                  onError={(e) => (e.currentTarget.style.display = "none")}
                                />
                              ) : null}

                              <span className={isDark ? "text-slate-300 text-sm" : "text-slate-700 text-sm"}>
                                {selectedProfileTrackType === "all"
                                  ? "Profile Score"
                                  : `${prettyTrackType(selectedProfileTrackType)} Score`}
                                :{" "}
                                <span className="font-bold">
                                  {p.selectedTrackTypeProfile != null
                                    ? p.selectedTrackTypeProfile.toFixed(1)
                                    : "—"}
                                </span>
                              </span>
                            </div>
                          </div>
                        </div>

                        <div
                          className={[
                            "mt-3 rounded-2xl border px-3 py-2.5",
                            T.border,
                            isDark ? "bg-slate-900/50" : "bg-slate-50",
                          ].join(" ")}
                        >
                          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-x-4 gap-y-2 text-sm">
                            <div>
                              <div className={isDark ? "text-slate-400 text-[11px]" : "text-slate-500 text-[11px]"}>
                                DK Sal
                              </div>
                              <div className="font-bold">{moneyLike(p.dkSal)}</div>
                            </div>
                            <div>
                              <div className={isDark ? "text-slate-400 text-[11px]" : "text-slate-500 text-[11px]"}>
                                DK Proj
                              </div>
                              <div className="font-bold">{p.proj?.dk != null ? oneDec(p.proj.dk) : "—"}</div>
                            </div>
                            <div>
                              <div className={isDark ? "text-slate-400 text-[11px]" : "text-slate-500 text-[11px]"}>
                                FD Sal
                              </div>
                              <div className="font-bold">{moneyLike(p.fdSal)}</div>
                            </div>
                            <div>
                              <div className={isDark ? "text-slate-400 text-[11px]" : "text-slate-500 text-[11px]"}>
                                FD Proj
                              </div>
                              <div className="font-bold">{p.proj?.fd != null ? oneDec(p.proj.fd) : "—"}</div>
                            </div>
                            <div>
                              <div className={isDark ? "text-slate-400 text-[11px]" : "text-slate-500 text-[11px]"}>
                                Qual
                              </div>
                              <div className="font-bold">{p.qual ?? "—"}</div>
                            </div>
                            <div>
                              <div className={isDark ? "text-slate-400 text-[11px]" : "text-slate-500 text-[11px]"}>
                                Odds
                              </div>
                              <div className="font-bold">{p.odds ?? "—"}</div>
                            </div>
                            <div>
                              <div className={isDark ? "text-slate-400 text-[11px]" : "text-slate-500 text-[11px]"}>
                                Practice
                              </div>
                              <div className="font-bold">{p.prac != null ? oneDec(p.prac) : "—"}</div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-3">
                          <div className="text-xs font-semibold text-sky-400 mb-2">Best Track Types</div>
                          <div className="flex flex-wrap gap-2">
                            {p.bestTypes.map((t) => (
                              <span
                                key={t.key}
                                className="inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold bg-sky-500/15 text-sky-300 border border-sky-400/20"
                              >
                                {t.label}: {t.value != null ? t.value.toFixed(0) : "—"} ({t.races})
                              </span>
                            ))}
                          </div>
                        </div>

                        {!!p.worstTypes.length && (
                          <div className="mt-3">
                            <div className="text-xs font-semibold text-amber-400 mb-2">Weakest Track Types</div>
                            <div className="flex flex-wrap gap-2">
                              {p.worstTypes.map((t) => (
                                <span
                                  key={t.key}
                                  className="inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold bg-amber-500/12 text-amber-300 border border-amber-400/20"
                                >
                                  {t.label}: {t.value != null ? t.value.toFixed(0) : "—"} ({t.races})
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="mt-4 flex items-center gap-2">
                          <ProfileTabPill
                            active={activeProfileTab === "radar"}
                            onClick={() => setProfileTabByDriver((prev) => ({ ...prev, [tabKey]: "radar" }))}
                            dark={isDark}
                          >
                            Radar
                          </ProfileTabPill>
                          <ProfileTabPill
                            active={activeProfileTab === "edges"}
                            onClick={() => setProfileTabByDriver((prev) => ({ ...prev, [tabKey]: "edges" }))}
                            dark={isDark}
                          >
                            Category Edges
                          </ProfileTabPill>
                        </div>

                        <div className="mt-4">
                          <button
                            type="button"
                            onClick={() => {
                              setRaceLogDriver(p.driver);
                              setRaceLogOpen(true);
                            }}
                            className="text-sky-400 font-semibold"
                          >
                            Open full race log →
                          </button>
                        </div>
                      </div>

                      <div className="w-full 2xl:w-[330px] shrink-0">
                        {activeProfileTab === "radar" ? (
                          <>
                            <div className="flex items-center justify-between gap-3 mb-2">
                              <div className="text-xs font-semibold text-sky-400">Track Type Profile</div>
                              <div className={"text-[10px] font-semibold uppercase tracking-wider " + (isDark ? "text-slate-500" : "text-slate-500")}>
                                0–100 percentile score
                              </div>
                            </div>
                            <div
                              className={
                                `rounded-2xl border ${T.border} p-4 ` +
                                (isDark
                                  ? "bg-gradient-to-b from-slate-900/80 to-slate-950/70 shadow-inner"
                                  : "bg-gradient-to-b from-white to-slate-50 shadow-sm")
                              }
                            >
                              <RadarChart data={p.radar} size={300} dark={isDark} />
                            </div>
                            <div className={`mt-3 rounded-2xl border ${T.border} p-3 text-xs`}>
                              <div className={isDark ? "text-slate-300" : "text-slate-700"}>
                                Scores compare each driver with the current field using finish, average running position, driver rating, passing, and other loop-data performance metrics. Track types with fewer than four races are automatically discounted.
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="text-xs font-semibold text-sky-400 mb-2">Category Edges</div>
                            <div className={`rounded-2xl border ${T.border} overflow-hidden`}>
                              <table className="w-full text-[11px]">
                                <thead className={isDark ? "bg-slate-900 text-slate-200" : "bg-slate-50 text-slate-800"}>
                                  <tr>
                                    <th className="px-3 py-2 text-left">Type</th>
                                    <th className="px-2 py-2 text-center">Score</th>
                                    <th className="px-2 py-2 text-center">vs Avg</th>
                                    <th className="px-2 py-2 text-center">vs Best</th>
                                    <th className="px-2 py-2 text-center">Races</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {p.categoryEdges.length ? (
                                    p.categoryEdges.map((edge, edgeIdx) => (
                                      <tr
                                        key={`${p.driver}-${edge.key}-${edgeIdx}`}
                                        className={edgeIdx % 2 ? "bg-slate-900/60" : "bg-slate-950/60"}
                                      >
                                        <td className="px-3 py-2 text-left text-slate-100 font-medium whitespace-nowrap">
                                          {edge.label}
                                        </td>
                                        <td className="px-2 py-2 text-center text-slate-100 font-semibold">
                                          {edge.value != null ? edge.value.toFixed(1) : "—"}
                                        </td>
                                        <td className="px-2 py-2 text-center text-slate-300 whitespace-nowrap">
                                          {edge.vsProfileAvgPct != null ? (
                                            <span className={edge.vsProfileAvgPct >= 0 ? "text-emerald-300" : "text-rose-300"}>
                                              {edge.vsProfileAvgPct >= 0 ? "+" : ""}
                                              {edge.vsProfileAvgPct.toFixed(1)}%
                                            </span>
                                          ) : (
                                            "—"
                                          )}
                                        </td>
                                        <td className="px-2 py-2 text-center text-slate-300 whitespace-nowrap">
                                          {edge.vsBestPct != null ? (
                                            <span className={edge.vsBestPct >= 0 ? "text-emerald-300" : "text-amber-300"}>
                                              {edge.vsBestPct >= 0 ? "+" : ""}
                                              {edge.vsBestPct.toFixed(1)}%
                                            </span>
                                          ) : (
                                            "—"
                                          )}
                                        </td>
                                        <td className="px-2 py-2 text-center text-slate-100">{edge.races ?? 0}</td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr>
                                      <td colSpan={5} className="px-3 py-4 text-center text-slate-400 text-sm">
                                        Not enough category data yet.
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                            <div className="mt-3 text-[11px] text-slate-400">
                              Based on how this driver grades in each track-type bucket versus his own profile average and best bucket.
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
                })}
              </div>
            )}

            {profileDisplay === "sheet" && (
              <DriverProfileSheet
                profileRows={!loading && !err ? profileRows : []}
                selectedProfileTrackType={selectedProfileTrackType}
                T={T}
                isDark={isDark}
                setRaceLogDriver={setRaceLogDriver}
                setRaceLogOpen={setRaceLogOpen}
              />
            )}
          </>
        )}

        <DriverRaceLogModal
          open={raceLogOpen}
          onClose={() => setRaceLogOpen(false)}
          driverName={raceLogDriver}
          seriesKey={series}
          raceLogs={raceLogModalRows}
        />
      </div>
    </div>
  );
}
